import { SUPPORTED_VERSION, KNOWN_KINDS, normalizeTies, DEFAULT_EXCLUDED_STATUSES, rank, normalizeStatus, measure, normalizeBetterWhen, parse, isOpenResult, UnsupportedVersionError } from "@openresult/core";
import { fullFormats } from "ajv-formats/dist/formats.js";
import ucs2lengthModule from "ajv/dist/runtime/ucs2length.js";
const CATALOGUE = {
  "OR-101": { severity: "error", rule: "spec §4.1.1" },
  "OR-102": { severity: "error", rule: "spec §5.2.1" },
  "OR-103": { severity: "error", rule: "spec §5.1.2" },
  "OR-104": { severity: "error", rule: "spec §5.4.1" },
  "OR-105": { severity: "error", rule: "spec §10.2.4" },
  "OR-106": { severity: "error", rule: "spec §4.6.1" },
  "OR-107": { severity: "error", rule: "spec §5.1.3" },
  "OR-108": { severity: "error", rule: "spec §7.3.2" },
  "OR-109": { severity: "error", rule: "spec §5.1.8" },
  "OR-110": { severity: "error", rule: "spec §5.3.7" },
  "OR-111": { severity: "error", rule: "spec §5.2.6" },
  "OR-201": { severity: "error", rule: "spec §7.1.1" },
  "OR-202": { severity: "error", rule: "spec §5.4.2" },
  "OR-203": { severity: "error", rule: "spec §7.1.3" },
  "OR-204": { severity: "error", rule: "spec §6.2.2" },
  "OR-205": { severity: "error", rule: "spec §7.3.1" },
  "OR-206": { severity: "error", rule: "spec §5.3.2" },
  "OR-301": { severity: "error", rule: "spec §8.2.2" },
  "OR-302": { severity: "error", rule: "spec §8.3.1" },
  "OR-303": { severity: "error", rule: "spec §7.2.3" },
  "OR-304": { severity: "error", rule: "spec §8.2.1" },
  "OR-305": { severity: "error", rule: "spec §8.2.2" },
  "OR-401": { severity: "error", rule: "spec §4.2.1" },
  "OR-402": { severity: "error", rule: "spec §11.4.1" },
  // OR-403 was reserved for "version must strictly increase for the same id".
  // Writing the conformance suite showed it cannot exist: the rule compares two
  // documents, and a validator sees one. The code stays retired rather than
  // reused — a published code is permanent (spec §12.2.1).
  "OR-901": { severity: "warning", rule: "spec §5.1.7" },
  "OR-902": { severity: "warning", rule: "spec §3.3.2" },
  "OR-903": { severity: "warning", rule: "spec §4.5.1" },
  "OR-904": { severity: "warning", rule: "spec §6.2.4" },
  "OR-905": { severity: "warning", rule: "spec §5.3.6" },
  "OR-906": { severity: "warning", rule: "spec §8.1.4" },
  "OR-907": { severity: "warning", rule: "spec §9.1.1" },
  "OR-908": { severity: "warning", rule: "spec §8.5.2" },
  "OR-909": { severity: "warning", rule: "spec §5.1.8" },
  "OR-910": { severity: "warning", rule: "spec §6.1.7" },
  "OR-911": { severity: "warning", rule: "spec §8.3.5" },
  "OR-912": { severity: "warning", rule: "spec §9.2.2" },
  "OR-914": { severity: "warning", rule: "spec §11.4.2" },
  "OR-913": { severity: "warning", rule: "spec §8.1.1" }
};
function diagnostic(code, path, message, suggestion) {
  const entry = CATALOGUE[code];
  return { code, severity: entry.severity, path, message, rule: entry.rule, suggestion };
}
function pointer(...segments) {
  if (segments.length === 0) return "";
  return `/${segments.map((segment) => String(segment).replace(/~/g, "~0").replace(/\//g, "~1")).join("/")}`;
}
const ucs2length = ucs2lengthModule.default ?? ucs2lengthModule;
const schema31 = { "$schema": "https://json-schema.org/draft/2020-12/schema", "$id": "https://openresult.dev/schema/openresult-1.0.schema.json", "title": "OpenResult 1.0", "description": "An open standard for describing results as JSON. See specification/openresult-v1.md — the specification is normative, this schema is its machine-readable expression.", "type": "object", "required": ["openresult", "title", "participants", "results"], "properties": { "openresult": { "type": "string", "pattern": "^\\d+\\.\\d+$", "description": "Format version, MAJOR.MINOR (spec §4.2.1)." }, "id": { "$ref": "#/$defs/identifier", "description": "Identifies the subject across publications (spec §4.3.1)." }, "version": { "type": "integer", "minimum": 0, "description": "Content version, strictly increasing per id (spec §4.4.2)." }, "status": { "type": "string", "enum": ["draft", "provisional", "official", "amended"], "description": "Content lifecycle (spec §4.4.1)." }, "title": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "lang": { "type": "string", "pattern": "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$", "description": "BCP 47 language tag (spec §4.5.1)." }, "generatedAt": { "$ref": "#/$defs/timestamp" }, "occurredAt": { "$ref": "#/$defs/timeRange" }, "source": { "$ref": "#/$defs/source" }, "measures": { "type": "array", "items": { "$ref": "#/$defs/measure" } }, "attributeDefinitions": { "type": "array", "items": { "$ref": "#/$defs/attributeDefinition" } }, "attributes": { "$ref": "#/$defs/attributeValues", "description": "Facts about the competition itself — a venue, an equipment division, the edition of the rules in force (spec §4.7.1)." }, "participants": { "type": "array", "items": { "$ref": "#/$defs/participant" } }, "events": { "type": "array", "items": { "$ref": "#/$defs/event" } }, "results": { "type": "array", "items": { "$ref": "#/$defs/result" } }, "rankings": { "type": "array", "items": { "$ref": "#/$defs/ranking" } }, "categories": { "type": "array", "items": { "$ref": "#/$defs/category" } }, "links": { "type": "array", "items": { "$ref": "#/$defs/link" } }, "assets": { "type": "array", "items": { "$ref": "#/$defs/asset" } }, "presentation": { "$ref": "#/$defs/presentation" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false, "$defs": { "identifier": { "type": "string", "pattern": "^[A-Za-z0-9_-]+$", "minLength": 1, "description": "Opaque producer-assigned identifier (spec §5.4)." }, "identifierList": { "type": "array", "items": { "$ref": "#/$defs/identifier" } }, "timestamp": { "type": "string", "anyOf": [{ "format": "date-time" }, { "format": "date" }], "pattern": "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$", "description": "RFC 3339 date-time with mandatory offset, or full-date (spec §4.6)." }, "timeRange": { "type": "object", "properties": { "start": { "$ref": "#/$defs/timestamp" }, "end": { "$ref": "#/$defs/timestamp" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "measure": { "type": "object", "required": ["id", "label", "kind", "betterWhen"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "kind": { "type": "string", "enum": ["duration", "distance", "mass", "points", "score", "percentage", "count", "money", "rate", "text", "boolean"] }, "unit": { "type": "string", "minLength": 1 }, "precision": { "type": "integer", "minimum": 0 }, "min": { "type": "number" }, "max": { "type": "number" }, "betterWhen": { "type": "string", "enum": ["lower", "higher", "none"] } }, "allOf": [{ "description": "unit is required unless kind is text or boolean (spec §5.1.3).", "if": { "properties": { "kind": { "enum": ["text", "boolean"] } }, "required": ["kind"] }, "then": true, "else": { "required": ["unit"] } }], "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "attributeDefinition": { "type": "object", "required": ["id", "label", "type"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "type": { "type": "string", "enum": ["text", "number", "date", "url", "country", "boolean"] }, "unit": { "type": "string", "minLength": 1 } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false, "allOf": [{ "description": "unit is only meaningful on a number attribute (spec §5.3.7).", "if": { "required": ["unit"] }, "then": { "properties": { "type": { "const": "number" } } } }] }, "attributeValues": { "type": "object", "description": "Keys reference declared attribute ids; values must match the declared type (spec §5.3.3).", "additionalProperties": { "type": ["string", "number", "boolean"] } }, "participant": { "type": "object", "required": ["id", "name"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "name": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "shortName": { "type": "string" }, "type": { "type": "string", "enum": ["person", "team", "machine", "product", "model", "organization", "other"], "default": "person" }, "members": { "$ref": "#/$defs/identifierList" }, "attributes": { "$ref": "#/$defs/attributeValues" }, "links": { "type": "array", "items": { "$ref": "#/$defs/link" } }, "assets": { "type": "array", "items": { "$ref": "#/$defs/asset" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "event": { "type": "object", "required": ["id", "name"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "name": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "type": { "type": "string", "enum": ["heat", "match", "round", "stage", "session", "final", "overall", "other"], "default": "other" }, "parent": { "$ref": "#/$defs/identifier" }, "occurredAt": { "$ref": "#/$defs/timeRange" }, "participants": { "$ref": "#/$defs/identifierList" }, "attributes": { "$ref": "#/$defs/attributeValues" }, "links": { "type": "array", "items": { "$ref": "#/$defs/link" } }, "assets": { "type": "array", "items": { "$ref": "#/$defs/asset" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "status": { "type": "string", "enum": ["finished", "bye", "notClassified", "inProgress", "dnf", "dns", "dsq", "outOfTime", "withdrawn"], "default": "finished" }, "result": { "type": "object", "required": ["participant"], "properties": { "participant": { "$ref": "#/$defs/identifier" }, "event": { "$ref": "#/$defs/identifier" }, "status": { "$ref": "#/$defs/status" }, "ranks": { "type": "object", "description": "Positions the producer published, keyed by ranking id. Informative: the ordering must be derivable without it (spec §7.5).", "propertyNames": { "$ref": "#/$defs/identifier" }, "additionalProperties": { "type": "integer", "minimum": 1 } }, "values": { "type": "object", "description": "Keys reference declared measure ids. null is forbidden: omit an unavailable measure (spec §7.3.2).", "additionalProperties": { "type": ["number", "string", "boolean"] } }, "attributes": { "$ref": "#/$defs/attributeValues" }, "notes": { "type": "string" }, "links": { "type": "array", "items": { "$ref": "#/$defs/link" } }, "assets": { "type": "array", "items": { "$ref": "#/$defs/asset" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "ranking": { "type": "object", "required": ["id", "label", "sortBy"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "scope": { "type": "object", "properties": { "event": { "oneOf": [{ "$ref": "#/$defs/identifier" }, { "$ref": "#/$defs/identifierList" }] }, "category": { "oneOf": [{ "$ref": "#/$defs/identifier" }, { "$ref": "#/$defs/identifierList" }] } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "sortBy": { "type": "array", "items": { "$ref": "#/$defs/identifier" }, "description": "Measure ids by decreasing priority. Direction comes from the measure (spec §8.2.3)." }, "ties": { "type": "string", "enum": ["standard", "dense", "strict", "resolved"], "default": "standard" }, "excludeStatuses": { "type": "array", "items": { "$ref": "#/$defs/status" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false, "allOf": [{ "description": "sortBy may only be empty when ties is resolved (spec §8.2.1).", "if": { "properties": { "ties": { "const": "resolved" } }, "required": ["ties"] }, "else": { "properties": { "sortBy": { "minItems": 1 } } } }] }, "category": { "type": "object", "required": ["id", "label"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "attributes": { "$ref": "#/$defs/attributeValues" }, "participants": { "$ref": "#/$defs/identifierList" }, "parent": { "$ref": "#/$defs/identifier" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "source": { "type": "object", "required": ["name"], "properties": { "name": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "system": { "type": "string" }, "url": { "type": "string", "format": "uri" }, "license": { "type": "string" }, "contact": { "type": "string" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "link": { "type": "object", "required": ["href"], "properties": { "rel": { "type": "string" }, "href": { "type": "string", "format": "uri" }, "label": { "type": "string" }, "description": { "type": "string" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "asset": { "type": "object", "required": ["href"], "properties": { "type": { "type": "string", "enum": ["image", "video", "audio", "document", "other"] }, "href": { "type": "string", "format": "uri" }, "label": { "type": "string" }, "description": { "type": "string" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "presentation": { "type": "object", "description": "Non-normative display hints. A consumer may ignore this object entirely (spec §10.1).", "properties": { "defaultView": { "type": "string" }, "measureOrder": { "$ref": "#/$defs/identifierList" }, "attributeOrder": { "$ref": "#/$defs/identifierList" }, "highlight": { "$ref": "#/$defs/identifierList" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false } } };
const schema32 = { "type": "string", "pattern": "^[A-Za-z0-9_-]+$", "minLength": 1, "description": "Opaque producer-assigned identifier (spec §5.4)." };
const schema33 = { "type": "string", "anyOf": [{ "format": "date-time" }, { "format": "date" }], "pattern": "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$", "description": "RFC 3339 date-time with mandatory offset, or full-date (spec §4.6)." };
const schema37 = { "type": "object", "required": ["name"], "properties": { "name": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "system": { "type": "string" }, "url": { "type": "string", "format": "uri" }, "license": { "type": "string" }, "contact": { "type": "string" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
const schema42 = { "type": "object", "description": "Keys reference declared attribute ids; values must match the declared type (spec §5.3.3).", "additionalProperties": { "type": ["string", "number", "boolean"] } };
const schema48 = { "type": "object", "required": ["href"], "properties": { "rel": { "type": "string" }, "href": { "type": "string", "format": "uri" }, "label": { "type": "string" }, "description": { "type": "string" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
const schema49 = { "type": "object", "required": ["href"], "properties": { "type": { "type": "string", "enum": ["image", "video", "audio", "document", "other"] }, "href": { "type": "string", "format": "uri" }, "label": { "type": "string" }, "description": { "type": "string" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
const pattern4 = new RegExp("^\\d+\\.\\d+$", "u");
const pattern5 = new RegExp("^[A-Za-z0-9_-]+$", "u");
const pattern6 = new RegExp("^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$", "u");
const pattern7 = new RegExp("^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$", "u");
const pattern10 = new RegExp("^x-", "u");
const func1 = ucs2length;
const formats0 = fullFormats["date-time"];
const formats2 = fullFormats.date;
const formats12 = fullFormats.uri;
const schema34 = { "type": "object", "properties": { "start": { "$ref": "#/$defs/timestamp" }, "end": { "$ref": "#/$defs/timestamp" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
function validate21(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate21.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.start !== void 0) {
      let data0 = data.start;
      const _errs4 = errors;
      let valid2 = false;
      const _errs5 = errors;
      if (typeof data0 === "string") {
        if (!formats0.validate(data0)) {
          const err0 = { instancePath: instancePath + "/start", schemaPath: "#/$defs/timestamp/anyOf/0/format", keyword: "format", params: { format: "date-time" }, message: 'must match format "date-time"', schema: "date-time", parentSchema: schema33.anyOf[0], data: data0 };
          if (vErrors === null) {
            vErrors = [err0];
          } else {
            vErrors.push(err0);
          }
          errors++;
        }
      }
      var _valid0 = _errs5 === errors;
      valid2 = valid2 || _valid0;
      const _errs6 = errors;
      if (typeof data0 === "string") {
        if (!formats2.validate(data0)) {
          const err1 = { instancePath: instancePath + "/start", schemaPath: "#/$defs/timestamp/anyOf/1/format", keyword: "format", params: { format: "date" }, message: 'must match format "date"', schema: "date", parentSchema: schema33.anyOf[1], data: data0 };
          if (vErrors === null) {
            vErrors = [err1];
          } else {
            vErrors.push(err1);
          }
          errors++;
        }
      }
      var _valid0 = _errs6 === errors;
      valid2 = valid2 || _valid0;
      if (!valid2) {
        const err2 = { instancePath: instancePath + "/start", schemaPath: "#/$defs/timestamp/anyOf", keyword: "anyOf", params: {}, message: "must match a schema in anyOf", schema: schema33.anyOf, parentSchema: schema33, data: data0 };
        if (vErrors === null) {
          vErrors = [err2];
        } else {
          vErrors.push(err2);
        }
        errors++;
      } else {
        errors = _errs4;
        if (vErrors !== null) {
          if (_errs4) {
            vErrors.length = _errs4;
          } else {
            vErrors = null;
          }
        }
      }
      if (typeof data0 === "string") {
        if (!pattern7.test(data0)) {
          const err3 = { instancePath: instancePath + "/start", schemaPath: "#/$defs/timestamp/pattern", keyword: "pattern", params: { pattern: "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$" }, message: 'must match pattern "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$"', schema: "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$", parentSchema: schema33, data: data0 };
          if (vErrors === null) {
            vErrors = [err3];
          } else {
            vErrors.push(err3);
          }
          errors++;
        }
      } else {
        const err4 = { instancePath: instancePath + "/start", schemaPath: "#/$defs/timestamp/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema33.type, parentSchema: schema33, data: data0 };
        if (vErrors === null) {
          vErrors = [err4];
        } else {
          vErrors.push(err4);
        }
        errors++;
      }
    }
    if (data.end !== void 0) {
      let data1 = data.end;
      const _errs10 = errors;
      let valid4 = false;
      const _errs11 = errors;
      if (typeof data1 === "string") {
        if (!formats0.validate(data1)) {
          const err5 = { instancePath: instancePath + "/end", schemaPath: "#/$defs/timestamp/anyOf/0/format", keyword: "format", params: { format: "date-time" }, message: 'must match format "date-time"', schema: "date-time", parentSchema: schema33.anyOf[0], data: data1 };
          if (vErrors === null) {
            vErrors = [err5];
          } else {
            vErrors.push(err5);
          }
          errors++;
        }
      }
      var _valid1 = _errs11 === errors;
      valid4 = valid4 || _valid1;
      const _errs12 = errors;
      if (typeof data1 === "string") {
        if (!formats2.validate(data1)) {
          const err6 = { instancePath: instancePath + "/end", schemaPath: "#/$defs/timestamp/anyOf/1/format", keyword: "format", params: { format: "date" }, message: 'must match format "date"', schema: "date", parentSchema: schema33.anyOf[1], data: data1 };
          if (vErrors === null) {
            vErrors = [err6];
          } else {
            vErrors.push(err6);
          }
          errors++;
        }
      }
      var _valid1 = _errs12 === errors;
      valid4 = valid4 || _valid1;
      if (!valid4) {
        const err7 = { instancePath: instancePath + "/end", schemaPath: "#/$defs/timestamp/anyOf", keyword: "anyOf", params: {}, message: "must match a schema in anyOf", schema: schema33.anyOf, parentSchema: schema33, data: data1 };
        if (vErrors === null) {
          vErrors = [err7];
        } else {
          vErrors.push(err7);
        }
        errors++;
      } else {
        errors = _errs10;
        if (vErrors !== null) {
          if (_errs10) {
            vErrors.length = _errs10;
          } else {
            vErrors = null;
          }
        }
      }
      if (typeof data1 === "string") {
        if (!pattern7.test(data1)) {
          const err8 = { instancePath: instancePath + "/end", schemaPath: "#/$defs/timestamp/pattern", keyword: "pattern", params: { pattern: "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$" }, message: 'must match pattern "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$"', schema: "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$", parentSchema: schema33, data: data1 };
          if (vErrors === null) {
            vErrors = [err8];
          } else {
            vErrors.push(err8);
          }
          errors++;
        }
      } else {
        const err9 = { instancePath: instancePath + "/end", schemaPath: "#/$defs/timestamp/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema33.type, parentSchema: schema33, data: data1 };
        if (vErrors === null) {
          vErrors = [err9];
        } else {
          vErrors.push(err9);
        }
        errors++;
      }
    }
    var props0 = {};
    props0.start = true;
    props0.end = true;
    for (const key0 in data) {
      if (pattern10.test(key0)) {
        props0[key0] = true;
      }
    }
    if (props0 !== true) {
      for (const key1 in data) {
        if (!props0 || !props0[key1]) {
          const err10 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key1 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema34, data };
          if (vErrors === null) {
            vErrors = [err10];
          } else {
            vErrors.push(err10);
          }
          errors++;
        }
      }
    }
  } else {
    const err11 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema34.type, parentSchema: schema34, data };
    if (vErrors === null) {
      vErrors = [err11];
    } else {
      vErrors.push(err11);
    }
    errors++;
  }
  validate21.errors = vErrors;
  return errors === 0;
}
validate21.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema38 = { "type": "object", "required": ["id", "label", "kind", "betterWhen"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "kind": { "type": "string", "enum": ["duration", "distance", "mass", "points", "score", "percentage", "count", "money", "rate", "text", "boolean"] }, "unit": { "type": "string", "minLength": 1 }, "precision": { "type": "integer", "minimum": 0 }, "min": { "type": "number" }, "max": { "type": "number" }, "betterWhen": { "type": "string", "enum": ["lower", "higher", "none"] } }, "allOf": [{ "description": "unit is required unless kind is text or boolean (spec §5.1.3).", "if": { "properties": { "kind": { "enum": ["text", "boolean"] } }, "required": ["kind"] }, "then": true, "else": { "required": ["unit"] } }], "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
function validate23(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate23.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  const _errs2 = errors;
  let valid1 = true;
  const _errs3 = errors;
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.kind === void 0 && "kind") {
      const err0 = {};
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    } else {
      if (data.kind !== void 0) {
        let data0 = data.kind;
        if (!(data0 === "text" || data0 === "boolean")) {
          const err1 = {};
          if (vErrors === null) {
            vErrors = [err1];
          } else {
            vErrors.push(err1);
          }
          errors++;
        }
      }
    }
  }
  var _valid0 = _errs3 === errors;
  errors = _errs2;
  if (vErrors !== null) {
    if (_errs2) {
      vErrors.length = _errs2;
    } else {
      vErrors = null;
    }
  }
  if (!_valid0) {
    const _errs5 = errors;
    if (data && typeof data == "object" && !Array.isArray(data)) {
      if (data.unit === void 0) {
        const err2 = { instancePath, schemaPath: "#/allOf/0/else/required", keyword: "required", params: { missingProperty: "unit" }, message: "must have required property 'unit'", schema: schema38.allOf[0].else.required, parentSchema: schema38.allOf[0].else, data };
        if (vErrors === null) {
          vErrors = [err2];
        } else {
          vErrors.push(err2);
        }
        errors++;
      }
    }
    var _valid0 = _errs5 === errors;
    valid1 = _valid0;
  }
  if (!valid1) {
    const err3 = { instancePath, schemaPath: "#/allOf/0/if", keyword: "if", params: { failingKeyword: "else" }, message: 'must match "else" schema', schema: schema38.allOf[0].if, parentSchema: schema38.allOf[0], data };
    if (vErrors === null) {
      vErrors = [err3];
    } else {
      vErrors.push(err3);
    }
    errors++;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.id === void 0) {
      const err4 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "id" }, message: "must have required property 'id'", schema: schema38.required, parentSchema: schema38, data };
      if (vErrors === null) {
        vErrors = [err4];
      } else {
        vErrors.push(err4);
      }
      errors++;
    }
    if (data.label === void 0) {
      const err5 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "label" }, message: "must have required property 'label'", schema: schema38.required, parentSchema: schema38, data };
      if (vErrors === null) {
        vErrors = [err5];
      } else {
        vErrors.push(err5);
      }
      errors++;
    }
    if (data.kind === void 0) {
      const err6 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "kind" }, message: "must have required property 'kind'", schema: schema38.required, parentSchema: schema38, data };
      if (vErrors === null) {
        vErrors = [err6];
      } else {
        vErrors.push(err6);
      }
      errors++;
    }
    if (data.betterWhen === void 0) {
      const err7 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "betterWhen" }, message: "must have required property 'betterWhen'", schema: schema38.required, parentSchema: schema38, data };
      if (vErrors === null) {
        vErrors = [err7];
      } else {
        vErrors.push(err7);
      }
      errors++;
    }
    if (data.id !== void 0) {
      let data1 = data.id;
      if (typeof data1 === "string") {
        if (func1(data1) < 1) {
          const err8 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err8];
          } else {
            vErrors.push(err8);
          }
          errors++;
        }
        if (!pattern5.test(data1)) {
          const err9 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err9];
          } else {
            vErrors.push(err9);
          }
          errors++;
        }
      } else {
        const err10 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data1 };
        if (vErrors === null) {
          vErrors = [err10];
        } else {
          vErrors.push(err10);
        }
        errors++;
      }
    }
    if (data.label !== void 0) {
      let data2 = data.label;
      if (typeof data2 === "string") {
        if (func1(data2) < 1) {
          const err11 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema38.properties.label, data: data2 };
          if (vErrors === null) {
            vErrors = [err11];
          } else {
            vErrors.push(err11);
          }
          errors++;
        }
      } else {
        const err12 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema38.properties.label.type, parentSchema: schema38.properties.label, data: data2 };
        if (vErrors === null) {
          vErrors = [err12];
        } else {
          vErrors.push(err12);
        }
        errors++;
      }
    }
    if (data.description !== void 0) {
      let data3 = data.description;
      if (typeof data3 !== "string") {
        const err13 = { instancePath: instancePath + "/description", schemaPath: "#/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema38.properties.description.type, parentSchema: schema38.properties.description, data: data3 };
        if (vErrors === null) {
          vErrors = [err13];
        } else {
          vErrors.push(err13);
        }
        errors++;
      }
    }
    if (data.kind !== void 0) {
      let data4 = data.kind;
      if (typeof data4 !== "string") {
        const err14 = { instancePath: instancePath + "/kind", schemaPath: "#/properties/kind/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema38.properties.kind.type, parentSchema: schema38.properties.kind, data: data4 };
        if (vErrors === null) {
          vErrors = [err14];
        } else {
          vErrors.push(err14);
        }
        errors++;
      }
      if (!(data4 === "duration" || data4 === "distance" || data4 === "mass" || data4 === "points" || data4 === "score" || data4 === "percentage" || data4 === "count" || data4 === "money" || data4 === "rate" || data4 === "text" || data4 === "boolean")) {
        const err15 = { instancePath: instancePath + "/kind", schemaPath: "#/properties/kind/enum", keyword: "enum", params: { allowedValues: schema38.properties.kind.enum }, message: "must be equal to one of the allowed values", schema: schema38.properties.kind.enum, parentSchema: schema38.properties.kind, data: data4 };
        if (vErrors === null) {
          vErrors = [err15];
        } else {
          vErrors.push(err15);
        }
        errors++;
      }
    }
    if (data.unit !== void 0) {
      let data5 = data.unit;
      if (typeof data5 === "string") {
        if (func1(data5) < 1) {
          const err16 = { instancePath: instancePath + "/unit", schemaPath: "#/properties/unit/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema38.properties.unit, data: data5 };
          if (vErrors === null) {
            vErrors = [err16];
          } else {
            vErrors.push(err16);
          }
          errors++;
        }
      } else {
        const err17 = { instancePath: instancePath + "/unit", schemaPath: "#/properties/unit/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema38.properties.unit.type, parentSchema: schema38.properties.unit, data: data5 };
        if (vErrors === null) {
          vErrors = [err17];
        } else {
          vErrors.push(err17);
        }
        errors++;
      }
    }
    if (data.precision !== void 0) {
      let data6 = data.precision;
      if (!(typeof data6 == "number" && (!(data6 % 1) && !isNaN(data6)))) {
        const err18 = { instancePath: instancePath + "/precision", schemaPath: "#/properties/precision/type", keyword: "type", params: { type: "integer" }, message: "must be integer", schema: schema38.properties.precision.type, parentSchema: schema38.properties.precision, data: data6 };
        if (vErrors === null) {
          vErrors = [err18];
        } else {
          vErrors.push(err18);
        }
        errors++;
      }
      if (typeof data6 == "number") {
        if (data6 < 0 || isNaN(data6)) {
          const err19 = { instancePath: instancePath + "/precision", schemaPath: "#/properties/precision/minimum", keyword: "minimum", params: { comparison: ">=", limit: 0 }, message: "must be >= 0", schema: 0, parentSchema: schema38.properties.precision, data: data6 };
          if (vErrors === null) {
            vErrors = [err19];
          } else {
            vErrors.push(err19);
          }
          errors++;
        }
      }
    }
    if (data.min !== void 0) {
      let data7 = data.min;
      if (!(typeof data7 == "number")) {
        const err20 = { instancePath: instancePath + "/min", schemaPath: "#/properties/min/type", keyword: "type", params: { type: "number" }, message: "must be number", schema: schema38.properties.min.type, parentSchema: schema38.properties.min, data: data7 };
        if (vErrors === null) {
          vErrors = [err20];
        } else {
          vErrors.push(err20);
        }
        errors++;
      }
    }
    if (data.max !== void 0) {
      let data8 = data.max;
      if (!(typeof data8 == "number")) {
        const err21 = { instancePath: instancePath + "/max", schemaPath: "#/properties/max/type", keyword: "type", params: { type: "number" }, message: "must be number", schema: schema38.properties.max.type, parentSchema: schema38.properties.max, data: data8 };
        if (vErrors === null) {
          vErrors = [err21];
        } else {
          vErrors.push(err21);
        }
        errors++;
      }
    }
    if (data.betterWhen !== void 0) {
      let data9 = data.betterWhen;
      if (typeof data9 !== "string") {
        const err22 = { instancePath: instancePath + "/betterWhen", schemaPath: "#/properties/betterWhen/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema38.properties.betterWhen.type, parentSchema: schema38.properties.betterWhen, data: data9 };
        if (vErrors === null) {
          vErrors = [err22];
        } else {
          vErrors.push(err22);
        }
        errors++;
      }
      if (!(data9 === "lower" || data9 === "higher" || data9 === "none")) {
        const err23 = { instancePath: instancePath + "/betterWhen", schemaPath: "#/properties/betterWhen/enum", keyword: "enum", params: { allowedValues: schema38.properties.betterWhen.enum }, message: "must be equal to one of the allowed values", schema: schema38.properties.betterWhen.enum, parentSchema: schema38.properties.betterWhen, data: data9 };
        if (vErrors === null) {
          vErrors = [err23];
        } else {
          vErrors.push(err23);
        }
        errors++;
      }
    }
    var props0 = {};
    props0.id = true;
    props0.label = true;
    props0.description = true;
    props0.kind = true;
    props0.unit = true;
    props0.precision = true;
    props0.min = true;
    props0.max = true;
    props0.betterWhen = true;
    for (const key0 in data) {
      if (pattern10.test(key0)) {
        props0[key0] = true;
      }
    }
    if (props0 !== true) {
      for (const key1 in data) {
        if (!props0 || !props0[key1]) {
          const err24 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key1 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema38, data };
          if (vErrors === null) {
            vErrors = [err24];
          } else {
            vErrors.push(err24);
          }
          errors++;
        }
      }
    }
  } else {
    const err25 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema38.type, parentSchema: schema38, data };
    if (vErrors === null) {
      vErrors = [err25];
    } else {
      vErrors.push(err25);
    }
    errors++;
  }
  validate23.errors = vErrors;
  return errors === 0;
}
validate23.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema40 = { "type": "object", "required": ["id", "label", "type"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "type": { "type": "string", "enum": ["text", "number", "date", "url", "country", "boolean"] }, "unit": { "type": "string", "minLength": 1 } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false, "allOf": [{ "description": "unit is only meaningful on a number attribute (spec §5.3.7).", "if": { "required": ["unit"] }, "then": { "properties": { "type": { "const": "number" } } } }] };
function validate25(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate25.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  const _errs2 = errors;
  let valid1 = true;
  const _errs3 = errors;
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.unit === void 0 && "unit") {
      const err0 = {};
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    }
  }
  var _valid0 = _errs3 === errors;
  errors = _errs2;
  if (vErrors !== null) {
    if (_errs2) {
      vErrors.length = _errs2;
    } else {
      vErrors = null;
    }
  }
  if (_valid0) {
    const _errs4 = errors;
    if (data && typeof data == "object" && !Array.isArray(data)) {
      if (data.type !== void 0) {
        let data0 = data.type;
        if ("number" !== data0) {
          const err1 = { instancePath: instancePath + "/type", schemaPath: "#/allOf/0/then/properties/type/const", keyword: "const", params: { allowedValue: "number" }, message: "must be equal to constant", schema: "number", parentSchema: schema40.allOf[0].then.properties.type, data: data0 };
          if (vErrors === null) {
            vErrors = [err1];
          } else {
            vErrors.push(err1);
          }
          errors++;
        }
      }
    }
    var _valid0 = _errs4 === errors;
    valid1 = _valid0;
    if (valid1) {
      var props0 = {};
      props0.type = true;
    }
  }
  if (!valid1) {
    const err2 = { instancePath, schemaPath: "#/allOf/0/if", keyword: "if", params: { failingKeyword: "then" }, message: 'must match "then" schema', schema: schema40.allOf[0].if, parentSchema: schema40.allOf[0], data };
    if (vErrors === null) {
      vErrors = [err2];
    } else {
      vErrors.push(err2);
    }
    errors++;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.id === void 0) {
      const err3 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "id" }, message: "must have required property 'id'", schema: schema40.required, parentSchema: schema40, data };
      if (vErrors === null) {
        vErrors = [err3];
      } else {
        vErrors.push(err3);
      }
      errors++;
    }
    if (data.label === void 0) {
      const err4 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "label" }, message: "must have required property 'label'", schema: schema40.required, parentSchema: schema40, data };
      if (vErrors === null) {
        vErrors = [err4];
      } else {
        vErrors.push(err4);
      }
      errors++;
    }
    if (data.type === void 0) {
      const err5 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "type" }, message: "must have required property 'type'", schema: schema40.required, parentSchema: schema40, data };
      if (vErrors === null) {
        vErrors = [err5];
      } else {
        vErrors.push(err5);
      }
      errors++;
    }
    if (props0 !== true) {
      props0 = props0 || {};
      props0.id = true;
      props0.label = true;
      props0.description = true;
      props0.type = true;
      props0.unit = true;
    }
    if (data.id !== void 0) {
      let data1 = data.id;
      if (typeof data1 === "string") {
        if (func1(data1) < 1) {
          const err6 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err6];
          } else {
            vErrors.push(err6);
          }
          errors++;
        }
        if (!pattern5.test(data1)) {
          const err7 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err7];
          } else {
            vErrors.push(err7);
          }
          errors++;
        }
      } else {
        const err8 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data1 };
        if (vErrors === null) {
          vErrors = [err8];
        } else {
          vErrors.push(err8);
        }
        errors++;
      }
    }
    if (data.label !== void 0) {
      let data2 = data.label;
      if (typeof data2 === "string") {
        if (func1(data2) < 1) {
          const err9 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema40.properties.label, data: data2 };
          if (vErrors === null) {
            vErrors = [err9];
          } else {
            vErrors.push(err9);
          }
          errors++;
        }
      } else {
        const err10 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema40.properties.label.type, parentSchema: schema40.properties.label, data: data2 };
        if (vErrors === null) {
          vErrors = [err10];
        } else {
          vErrors.push(err10);
        }
        errors++;
      }
    }
    if (data.description !== void 0) {
      let data3 = data.description;
      if (typeof data3 !== "string") {
        const err11 = { instancePath: instancePath + "/description", schemaPath: "#/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema40.properties.description.type, parentSchema: schema40.properties.description, data: data3 };
        if (vErrors === null) {
          vErrors = [err11];
        } else {
          vErrors.push(err11);
        }
        errors++;
      }
    }
    if (data.type !== void 0) {
      let data4 = data.type;
      if (typeof data4 !== "string") {
        const err12 = { instancePath: instancePath + "/type", schemaPath: "#/properties/type/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema40.properties.type.type, parentSchema: schema40.properties.type, data: data4 };
        if (vErrors === null) {
          vErrors = [err12];
        } else {
          vErrors.push(err12);
        }
        errors++;
      }
      if (!(data4 === "text" || data4 === "number" || data4 === "date" || data4 === "url" || data4 === "country" || data4 === "boolean")) {
        const err13 = { instancePath: instancePath + "/type", schemaPath: "#/properties/type/enum", keyword: "enum", params: { allowedValues: schema40.properties.type.enum }, message: "must be equal to one of the allowed values", schema: schema40.properties.type.enum, parentSchema: schema40.properties.type, data: data4 };
        if (vErrors === null) {
          vErrors = [err13];
        } else {
          vErrors.push(err13);
        }
        errors++;
      }
    }
    if (data.unit !== void 0) {
      let data5 = data.unit;
      if (typeof data5 === "string") {
        if (func1(data5) < 1) {
          const err14 = { instancePath: instancePath + "/unit", schemaPath: "#/properties/unit/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema40.properties.unit, data: data5 };
          if (vErrors === null) {
            vErrors = [err14];
          } else {
            vErrors.push(err14);
          }
          errors++;
        }
      } else {
        const err15 = { instancePath: instancePath + "/unit", schemaPath: "#/properties/unit/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema40.properties.unit.type, parentSchema: schema40.properties.unit, data: data5 };
        if (vErrors === null) {
          vErrors = [err15];
        } else {
          vErrors.push(err15);
        }
        errors++;
      }
    }
    for (const key0 in data) {
      if (pattern10.test(key0)) {
        props0[key0] = true;
      }
    }
    if (props0 !== true) {
      for (const key1 in data) {
        if (!props0 || !props0[key1]) {
          const err16 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key1 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema40, data };
          if (vErrors === null) {
            vErrors = [err16];
          } else {
            vErrors.push(err16);
          }
          errors++;
        }
      }
    }
  } else {
    const err17 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema40.type, parentSchema: schema40, data };
    if (vErrors === null) {
      vErrors = [err17];
    } else {
      vErrors.push(err17);
    }
    errors++;
  }
  validate25.errors = vErrors;
  return errors === 0;
}
validate25.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema43 = { "type": "object", "required": ["id", "name"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "name": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "shortName": { "type": "string" }, "type": { "type": "string", "enum": ["person", "team", "machine", "product", "model", "organization", "other"], "default": "person" }, "members": { "$ref": "#/$defs/identifierList" }, "attributes": { "$ref": "#/$defs/attributeValues" }, "links": { "type": "array", "items": { "$ref": "#/$defs/link" } }, "assets": { "type": "array", "items": { "$ref": "#/$defs/asset" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
const schema45 = { "type": "array", "items": { "$ref": "#/$defs/identifier" } };
function validate28(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate28.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (Array.isArray(data)) {
    const len0 = data.length;
    for (let i0 = 0; i0 < len0; i0++) {
      let data0 = data[i0];
      if (typeof data0 === "string") {
        if (func1(data0) < 1) {
          const err0 = { instancePath: instancePath + "/" + i0, schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err0];
          } else {
            vErrors.push(err0);
          }
          errors++;
        }
        if (!pattern5.test(data0)) {
          const err1 = { instancePath: instancePath + "/" + i0, schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err1];
          } else {
            vErrors.push(err1);
          }
          errors++;
        }
      } else {
        const err2 = { instancePath: instancePath + "/" + i0, schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data0 };
        if (vErrors === null) {
          vErrors = [err2];
        } else {
          vErrors.push(err2);
        }
        errors++;
      }
    }
  } else {
    const err3 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema45.type, parentSchema: schema45, data };
    if (vErrors === null) {
      vErrors = [err3];
    } else {
      vErrors.push(err3);
    }
    errors++;
  }
  validate28.errors = vErrors;
  return errors === 0;
}
validate28.evaluated = { "items": true, "dynamicProps": false, "dynamicItems": false };
function validate27(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate27.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.id === void 0) {
      const err0 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "id" }, message: "must have required property 'id'", schema: schema43.required, parentSchema: schema43, data };
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    }
    if (data.name === void 0) {
      const err1 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "name" }, message: "must have required property 'name'", schema: schema43.required, parentSchema: schema43, data };
      if (vErrors === null) {
        vErrors = [err1];
      } else {
        vErrors.push(err1);
      }
      errors++;
    }
    if (data.id !== void 0) {
      let data0 = data.id;
      if (typeof data0 === "string") {
        if (func1(data0) < 1) {
          const err2 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err2];
          } else {
            vErrors.push(err2);
          }
          errors++;
        }
        if (!pattern5.test(data0)) {
          const err3 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err3];
          } else {
            vErrors.push(err3);
          }
          errors++;
        }
      } else {
        const err4 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data0 };
        if (vErrors === null) {
          vErrors = [err4];
        } else {
          vErrors.push(err4);
        }
        errors++;
      }
    }
    if (data.name !== void 0) {
      let data1 = data.name;
      if (typeof data1 === "string") {
        if (func1(data1) < 1) {
          const err5 = { instancePath: instancePath + "/name", schemaPath: "#/properties/name/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema43.properties.name, data: data1 };
          if (vErrors === null) {
            vErrors = [err5];
          } else {
            vErrors.push(err5);
          }
          errors++;
        }
      } else {
        const err6 = { instancePath: instancePath + "/name", schemaPath: "#/properties/name/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema43.properties.name.type, parentSchema: schema43.properties.name, data: data1 };
        if (vErrors === null) {
          vErrors = [err6];
        } else {
          vErrors.push(err6);
        }
        errors++;
      }
    }
    if (data.description !== void 0) {
      let data2 = data.description;
      if (typeof data2 !== "string") {
        const err7 = { instancePath: instancePath + "/description", schemaPath: "#/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema43.properties.description.type, parentSchema: schema43.properties.description, data: data2 };
        if (vErrors === null) {
          vErrors = [err7];
        } else {
          vErrors.push(err7);
        }
        errors++;
      }
    }
    if (data.shortName !== void 0) {
      let data3 = data.shortName;
      if (typeof data3 !== "string") {
        const err8 = { instancePath: instancePath + "/shortName", schemaPath: "#/properties/shortName/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema43.properties.shortName.type, parentSchema: schema43.properties.shortName, data: data3 };
        if (vErrors === null) {
          vErrors = [err8];
        } else {
          vErrors.push(err8);
        }
        errors++;
      }
    }
    if (data.type !== void 0) {
      let data4 = data.type;
      if (typeof data4 !== "string") {
        const err9 = { instancePath: instancePath + "/type", schemaPath: "#/properties/type/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema43.properties.type.type, parentSchema: schema43.properties.type, data: data4 };
        if (vErrors === null) {
          vErrors = [err9];
        } else {
          vErrors.push(err9);
        }
        errors++;
      }
      if (!(data4 === "person" || data4 === "team" || data4 === "machine" || data4 === "product" || data4 === "model" || data4 === "organization" || data4 === "other")) {
        const err10 = { instancePath: instancePath + "/type", schemaPath: "#/properties/type/enum", keyword: "enum", params: { allowedValues: schema43.properties.type.enum }, message: "must be equal to one of the allowed values", schema: schema43.properties.type.enum, parentSchema: schema43.properties.type, data: data4 };
        if (vErrors === null) {
          vErrors = [err10];
        } else {
          vErrors.push(err10);
        }
        errors++;
      }
    }
    if (data.members !== void 0) {
      if (!validate28(data.members, { instancePath: instancePath + "/members", parentData: data, parentDataProperty: "members", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
        errors = vErrors.length;
      }
    }
    if (data.attributes !== void 0) {
      let data6 = data.attributes;
      if (data6 && typeof data6 == "object" && !Array.isArray(data6)) {
        for (const key0 in data6) {
          let data7 = data6[key0];
          if (typeof data7 !== "string" && !(typeof data7 == "number") && typeof data7 !== "boolean") {
            const err11 = { instancePath: instancePath + "/attributes/" + key0.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/$defs/attributeValues/additionalProperties/type", keyword: "type", params: { type: schema42.additionalProperties.type }, message: "must be string,number,boolean", schema: schema42.additionalProperties.type, parentSchema: schema42.additionalProperties, data: data7 };
            if (vErrors === null) {
              vErrors = [err11];
            } else {
              vErrors.push(err11);
            }
            errors++;
          }
        }
      } else {
        const err12 = { instancePath: instancePath + "/attributes", schemaPath: "#/$defs/attributeValues/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema42.type, parentSchema: schema42, data: data6 };
        if (vErrors === null) {
          vErrors = [err12];
        } else {
          vErrors.push(err12);
        }
        errors++;
      }
    }
    if (data.links !== void 0) {
      let data8 = data.links;
      if (Array.isArray(data8)) {
        const len0 = data8.length;
        for (let i0 = 0; i0 < len0; i0++) {
          let data9 = data8[i0];
          if (data9 && typeof data9 == "object" && !Array.isArray(data9)) {
            if (data9.href === void 0) {
              const err13 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema48.required, parentSchema: schema48, data: data9 };
              if (vErrors === null) {
                vErrors = [err13];
              } else {
                vErrors.push(err13);
              }
              errors++;
            }
            if (data9.rel !== void 0) {
              let data10 = data9.rel;
              if (typeof data10 !== "string") {
                const err14 = { instancePath: instancePath + "/links/" + i0 + "/rel", schemaPath: "#/$defs/link/properties/rel/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.rel.type, parentSchema: schema48.properties.rel, data: data10 };
                if (vErrors === null) {
                  vErrors = [err14];
                } else {
                  vErrors.push(err14);
                }
                errors++;
              }
            }
            if (data9.href !== void 0) {
              let data11 = data9.href;
              if (typeof data11 === "string") {
                if (!formats12(data11)) {
                  const err15 = { instancePath: instancePath + "/links/" + i0 + "/href", schemaPath: "#/$defs/link/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema48.properties.href, data: data11 };
                  if (vErrors === null) {
                    vErrors = [err15];
                  } else {
                    vErrors.push(err15);
                  }
                  errors++;
                }
              } else {
                const err16 = { instancePath: instancePath + "/links/" + i0 + "/href", schemaPath: "#/$defs/link/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.href.type, parentSchema: schema48.properties.href, data: data11 };
                if (vErrors === null) {
                  vErrors = [err16];
                } else {
                  vErrors.push(err16);
                }
                errors++;
              }
            }
            if (data9.label !== void 0) {
              let data12 = data9.label;
              if (typeof data12 !== "string") {
                const err17 = { instancePath: instancePath + "/links/" + i0 + "/label", schemaPath: "#/$defs/link/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.label.type, parentSchema: schema48.properties.label, data: data12 };
                if (vErrors === null) {
                  vErrors = [err17];
                } else {
                  vErrors.push(err17);
                }
                errors++;
              }
            }
            if (data9.description !== void 0) {
              let data13 = data9.description;
              if (typeof data13 !== "string") {
                const err18 = { instancePath: instancePath + "/links/" + i0 + "/description", schemaPath: "#/$defs/link/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.description.type, parentSchema: schema48.properties.description, data: data13 };
                if (vErrors === null) {
                  vErrors = [err18];
                } else {
                  vErrors.push(err18);
                }
                errors++;
              }
            }
            var props0 = {};
            props0.rel = true;
            props0.href = true;
            props0.label = true;
            props0.description = true;
            for (const key1 in data9) {
              if (pattern10.test(key1)) {
                props0[key1] = true;
              }
            }
            if (props0 !== true) {
              for (const key2 in data9) {
                if (!props0 || !props0[key2]) {
                  const err19 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key2 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema48, data: data9 };
                  if (vErrors === null) {
                    vErrors = [err19];
                  } else {
                    vErrors.push(err19);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err20 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema48.type, parentSchema: schema48, data: data9 };
            if (vErrors === null) {
              vErrors = [err20];
            } else {
              vErrors.push(err20);
            }
            errors++;
          }
        }
      } else {
        const err21 = { instancePath: instancePath + "/links", schemaPath: "#/properties/links/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema43.properties.links.type, parentSchema: schema43.properties.links, data: data8 };
        if (vErrors === null) {
          vErrors = [err21];
        } else {
          vErrors.push(err21);
        }
        errors++;
      }
    }
    if (data.assets !== void 0) {
      let data14 = data.assets;
      if (Array.isArray(data14)) {
        const len1 = data14.length;
        for (let i1 = 0; i1 < len1; i1++) {
          let data15 = data14[i1];
          if (data15 && typeof data15 == "object" && !Array.isArray(data15)) {
            if (data15.href === void 0) {
              const err22 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema49.required, parentSchema: schema49, data: data15 };
              if (vErrors === null) {
                vErrors = [err22];
              } else {
                vErrors.push(err22);
              }
              errors++;
            }
            if (data15.type !== void 0) {
              let data16 = data15.type;
              if (typeof data16 !== "string") {
                const err23 = { instancePath: instancePath + "/assets/" + i1 + "/type", schemaPath: "#/$defs/asset/properties/type/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.type.type, parentSchema: schema49.properties.type, data: data16 };
                if (vErrors === null) {
                  vErrors = [err23];
                } else {
                  vErrors.push(err23);
                }
                errors++;
              }
              if (!(data16 === "image" || data16 === "video" || data16 === "audio" || data16 === "document" || data16 === "other")) {
                const err24 = { instancePath: instancePath + "/assets/" + i1 + "/type", schemaPath: "#/$defs/asset/properties/type/enum", keyword: "enum", params: { allowedValues: schema49.properties.type.enum }, message: "must be equal to one of the allowed values", schema: schema49.properties.type.enum, parentSchema: schema49.properties.type, data: data16 };
                if (vErrors === null) {
                  vErrors = [err24];
                } else {
                  vErrors.push(err24);
                }
                errors++;
              }
            }
            if (data15.href !== void 0) {
              let data17 = data15.href;
              if (typeof data17 === "string") {
                if (!formats12(data17)) {
                  const err25 = { instancePath: instancePath + "/assets/" + i1 + "/href", schemaPath: "#/$defs/asset/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema49.properties.href, data: data17 };
                  if (vErrors === null) {
                    vErrors = [err25];
                  } else {
                    vErrors.push(err25);
                  }
                  errors++;
                }
              } else {
                const err26 = { instancePath: instancePath + "/assets/" + i1 + "/href", schemaPath: "#/$defs/asset/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.href.type, parentSchema: schema49.properties.href, data: data17 };
                if (vErrors === null) {
                  vErrors = [err26];
                } else {
                  vErrors.push(err26);
                }
                errors++;
              }
            }
            if (data15.label !== void 0) {
              let data18 = data15.label;
              if (typeof data18 !== "string") {
                const err27 = { instancePath: instancePath + "/assets/" + i1 + "/label", schemaPath: "#/$defs/asset/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.label.type, parentSchema: schema49.properties.label, data: data18 };
                if (vErrors === null) {
                  vErrors = [err27];
                } else {
                  vErrors.push(err27);
                }
                errors++;
              }
            }
            if (data15.description !== void 0) {
              let data19 = data15.description;
              if (typeof data19 !== "string") {
                const err28 = { instancePath: instancePath + "/assets/" + i1 + "/description", schemaPath: "#/$defs/asset/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.description.type, parentSchema: schema49.properties.description, data: data19 };
                if (vErrors === null) {
                  vErrors = [err28];
                } else {
                  vErrors.push(err28);
                }
                errors++;
              }
            }
            var props1 = {};
            props1.type = true;
            props1.href = true;
            props1.label = true;
            props1.description = true;
            for (const key3 in data15) {
              if (pattern10.test(key3)) {
                props1[key3] = true;
              }
            }
            if (props1 !== true) {
              for (const key4 in data15) {
                if (!props1 || !props1[key4]) {
                  const err29 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key4 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema49, data: data15 };
                  if (vErrors === null) {
                    vErrors = [err29];
                  } else {
                    vErrors.push(err29);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err30 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema49.type, parentSchema: schema49, data: data15 };
            if (vErrors === null) {
              vErrors = [err30];
            } else {
              vErrors.push(err30);
            }
            errors++;
          }
        }
      } else {
        const err31 = { instancePath: instancePath + "/assets", schemaPath: "#/properties/assets/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema43.properties.assets.type, parentSchema: schema43.properties.assets, data: data14 };
        if (vErrors === null) {
          vErrors = [err31];
        } else {
          vErrors.push(err31);
        }
        errors++;
      }
    }
    var props2 = {};
    props2.id = true;
    props2.name = true;
    props2.description = true;
    props2.shortName = true;
    props2.type = true;
    props2.members = true;
    props2.attributes = true;
    props2.links = true;
    props2.assets = true;
    for (const key5 in data) {
      if (pattern10.test(key5)) {
        props2[key5] = true;
      }
    }
    if (props2 !== true) {
      for (const key6 in data) {
        if (!props2 || !props2[key6]) {
          const err32 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key6 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema43, data };
          if (vErrors === null) {
            vErrors = [err32];
          } else {
            vErrors.push(err32);
          }
          errors++;
        }
      }
    }
  } else {
    const err33 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema43.type, parentSchema: schema43, data };
    if (vErrors === null) {
      vErrors = [err33];
    } else {
      vErrors.push(err33);
    }
    errors++;
  }
  validate27.errors = vErrors;
  return errors === 0;
}
validate27.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema50 = { "type": "object", "required": ["id", "name"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "name": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "type": { "type": "string", "enum": ["heat", "match", "round", "stage", "session", "final", "overall", "other"], "default": "other" }, "parent": { "$ref": "#/$defs/identifier" }, "occurredAt": { "$ref": "#/$defs/timeRange" }, "participants": { "$ref": "#/$defs/identifierList" }, "attributes": { "$ref": "#/$defs/attributeValues" }, "links": { "type": "array", "items": { "$ref": "#/$defs/link" } }, "assets": { "type": "array", "items": { "$ref": "#/$defs/asset" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
function validate31(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate31.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.id === void 0) {
      const err0 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "id" }, message: "must have required property 'id'", schema: schema50.required, parentSchema: schema50, data };
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    }
    if (data.name === void 0) {
      const err1 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "name" }, message: "must have required property 'name'", schema: schema50.required, parentSchema: schema50, data };
      if (vErrors === null) {
        vErrors = [err1];
      } else {
        vErrors.push(err1);
      }
      errors++;
    }
    if (data.id !== void 0) {
      let data0 = data.id;
      if (typeof data0 === "string") {
        if (func1(data0) < 1) {
          const err2 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err2];
          } else {
            vErrors.push(err2);
          }
          errors++;
        }
        if (!pattern5.test(data0)) {
          const err3 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err3];
          } else {
            vErrors.push(err3);
          }
          errors++;
        }
      } else {
        const err4 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data0 };
        if (vErrors === null) {
          vErrors = [err4];
        } else {
          vErrors.push(err4);
        }
        errors++;
      }
    }
    if (data.name !== void 0) {
      let data1 = data.name;
      if (typeof data1 === "string") {
        if (func1(data1) < 1) {
          const err5 = { instancePath: instancePath + "/name", schemaPath: "#/properties/name/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema50.properties.name, data: data1 };
          if (vErrors === null) {
            vErrors = [err5];
          } else {
            vErrors.push(err5);
          }
          errors++;
        }
      } else {
        const err6 = { instancePath: instancePath + "/name", schemaPath: "#/properties/name/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema50.properties.name.type, parentSchema: schema50.properties.name, data: data1 };
        if (vErrors === null) {
          vErrors = [err6];
        } else {
          vErrors.push(err6);
        }
        errors++;
      }
    }
    if (data.description !== void 0) {
      let data2 = data.description;
      if (typeof data2 !== "string") {
        const err7 = { instancePath: instancePath + "/description", schemaPath: "#/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema50.properties.description.type, parentSchema: schema50.properties.description, data: data2 };
        if (vErrors === null) {
          vErrors = [err7];
        } else {
          vErrors.push(err7);
        }
        errors++;
      }
    }
    if (data.type !== void 0) {
      let data3 = data.type;
      if (typeof data3 !== "string") {
        const err8 = { instancePath: instancePath + "/type", schemaPath: "#/properties/type/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema50.properties.type.type, parentSchema: schema50.properties.type, data: data3 };
        if (vErrors === null) {
          vErrors = [err8];
        } else {
          vErrors.push(err8);
        }
        errors++;
      }
      if (!(data3 === "heat" || data3 === "match" || data3 === "round" || data3 === "stage" || data3 === "session" || data3 === "final" || data3 === "overall" || data3 === "other")) {
        const err9 = { instancePath: instancePath + "/type", schemaPath: "#/properties/type/enum", keyword: "enum", params: { allowedValues: schema50.properties.type.enum }, message: "must be equal to one of the allowed values", schema: schema50.properties.type.enum, parentSchema: schema50.properties.type, data: data3 };
        if (vErrors === null) {
          vErrors = [err9];
        } else {
          vErrors.push(err9);
        }
        errors++;
      }
    }
    if (data.parent !== void 0) {
      let data4 = data.parent;
      if (typeof data4 === "string") {
        if (func1(data4) < 1) {
          const err10 = { instancePath: instancePath + "/parent", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data4 };
          if (vErrors === null) {
            vErrors = [err10];
          } else {
            vErrors.push(err10);
          }
          errors++;
        }
        if (!pattern5.test(data4)) {
          const err11 = { instancePath: instancePath + "/parent", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data4 };
          if (vErrors === null) {
            vErrors = [err11];
          } else {
            vErrors.push(err11);
          }
          errors++;
        }
      } else {
        const err12 = { instancePath: instancePath + "/parent", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data4 };
        if (vErrors === null) {
          vErrors = [err12];
        } else {
          vErrors.push(err12);
        }
        errors++;
      }
    }
    if (data.occurredAt !== void 0) {
      if (!validate21(data.occurredAt, { instancePath: instancePath + "/occurredAt", parentData: data, parentDataProperty: "occurredAt", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate21.errors : vErrors.concat(validate21.errors);
        errors = vErrors.length;
      }
    }
    if (data.participants !== void 0) {
      if (!validate28(data.participants, { instancePath: instancePath + "/participants", parentData: data, parentDataProperty: "participants", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
        errors = vErrors.length;
      }
    }
    if (data.attributes !== void 0) {
      let data7 = data.attributes;
      if (data7 && typeof data7 == "object" && !Array.isArray(data7)) {
        for (const key0 in data7) {
          let data8 = data7[key0];
          if (typeof data8 !== "string" && !(typeof data8 == "number") && typeof data8 !== "boolean") {
            const err13 = { instancePath: instancePath + "/attributes/" + key0.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/$defs/attributeValues/additionalProperties/type", keyword: "type", params: { type: schema42.additionalProperties.type }, message: "must be string,number,boolean", schema: schema42.additionalProperties.type, parentSchema: schema42.additionalProperties, data: data8 };
            if (vErrors === null) {
              vErrors = [err13];
            } else {
              vErrors.push(err13);
            }
            errors++;
          }
        }
      } else {
        const err14 = { instancePath: instancePath + "/attributes", schemaPath: "#/$defs/attributeValues/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema42.type, parentSchema: schema42, data: data7 };
        if (vErrors === null) {
          vErrors = [err14];
        } else {
          vErrors.push(err14);
        }
        errors++;
      }
    }
    if (data.links !== void 0) {
      let data9 = data.links;
      if (Array.isArray(data9)) {
        const len0 = data9.length;
        for (let i0 = 0; i0 < len0; i0++) {
          let data10 = data9[i0];
          if (data10 && typeof data10 == "object" && !Array.isArray(data10)) {
            if (data10.href === void 0) {
              const err15 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema48.required, parentSchema: schema48, data: data10 };
              if (vErrors === null) {
                vErrors = [err15];
              } else {
                vErrors.push(err15);
              }
              errors++;
            }
            if (data10.rel !== void 0) {
              let data11 = data10.rel;
              if (typeof data11 !== "string") {
                const err16 = { instancePath: instancePath + "/links/" + i0 + "/rel", schemaPath: "#/$defs/link/properties/rel/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.rel.type, parentSchema: schema48.properties.rel, data: data11 };
                if (vErrors === null) {
                  vErrors = [err16];
                } else {
                  vErrors.push(err16);
                }
                errors++;
              }
            }
            if (data10.href !== void 0) {
              let data12 = data10.href;
              if (typeof data12 === "string") {
                if (!formats12(data12)) {
                  const err17 = { instancePath: instancePath + "/links/" + i0 + "/href", schemaPath: "#/$defs/link/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema48.properties.href, data: data12 };
                  if (vErrors === null) {
                    vErrors = [err17];
                  } else {
                    vErrors.push(err17);
                  }
                  errors++;
                }
              } else {
                const err18 = { instancePath: instancePath + "/links/" + i0 + "/href", schemaPath: "#/$defs/link/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.href.type, parentSchema: schema48.properties.href, data: data12 };
                if (vErrors === null) {
                  vErrors = [err18];
                } else {
                  vErrors.push(err18);
                }
                errors++;
              }
            }
            if (data10.label !== void 0) {
              let data13 = data10.label;
              if (typeof data13 !== "string") {
                const err19 = { instancePath: instancePath + "/links/" + i0 + "/label", schemaPath: "#/$defs/link/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.label.type, parentSchema: schema48.properties.label, data: data13 };
                if (vErrors === null) {
                  vErrors = [err19];
                } else {
                  vErrors.push(err19);
                }
                errors++;
              }
            }
            if (data10.description !== void 0) {
              let data14 = data10.description;
              if (typeof data14 !== "string") {
                const err20 = { instancePath: instancePath + "/links/" + i0 + "/description", schemaPath: "#/$defs/link/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.description.type, parentSchema: schema48.properties.description, data: data14 };
                if (vErrors === null) {
                  vErrors = [err20];
                } else {
                  vErrors.push(err20);
                }
                errors++;
              }
            }
            var props0 = {};
            props0.rel = true;
            props0.href = true;
            props0.label = true;
            props0.description = true;
            for (const key1 in data10) {
              if (pattern10.test(key1)) {
                props0[key1] = true;
              }
            }
            if (props0 !== true) {
              for (const key2 in data10) {
                if (!props0 || !props0[key2]) {
                  const err21 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key2 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema48, data: data10 };
                  if (vErrors === null) {
                    vErrors = [err21];
                  } else {
                    vErrors.push(err21);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err22 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema48.type, parentSchema: schema48, data: data10 };
            if (vErrors === null) {
              vErrors = [err22];
            } else {
              vErrors.push(err22);
            }
            errors++;
          }
        }
      } else {
        const err23 = { instancePath: instancePath + "/links", schemaPath: "#/properties/links/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema50.properties.links.type, parentSchema: schema50.properties.links, data: data9 };
        if (vErrors === null) {
          vErrors = [err23];
        } else {
          vErrors.push(err23);
        }
        errors++;
      }
    }
    if (data.assets !== void 0) {
      let data15 = data.assets;
      if (Array.isArray(data15)) {
        const len1 = data15.length;
        for (let i1 = 0; i1 < len1; i1++) {
          let data16 = data15[i1];
          if (data16 && typeof data16 == "object" && !Array.isArray(data16)) {
            if (data16.href === void 0) {
              const err24 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema49.required, parentSchema: schema49, data: data16 };
              if (vErrors === null) {
                vErrors = [err24];
              } else {
                vErrors.push(err24);
              }
              errors++;
            }
            if (data16.type !== void 0) {
              let data17 = data16.type;
              if (typeof data17 !== "string") {
                const err25 = { instancePath: instancePath + "/assets/" + i1 + "/type", schemaPath: "#/$defs/asset/properties/type/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.type.type, parentSchema: schema49.properties.type, data: data17 };
                if (vErrors === null) {
                  vErrors = [err25];
                } else {
                  vErrors.push(err25);
                }
                errors++;
              }
              if (!(data17 === "image" || data17 === "video" || data17 === "audio" || data17 === "document" || data17 === "other")) {
                const err26 = { instancePath: instancePath + "/assets/" + i1 + "/type", schemaPath: "#/$defs/asset/properties/type/enum", keyword: "enum", params: { allowedValues: schema49.properties.type.enum }, message: "must be equal to one of the allowed values", schema: schema49.properties.type.enum, parentSchema: schema49.properties.type, data: data17 };
                if (vErrors === null) {
                  vErrors = [err26];
                } else {
                  vErrors.push(err26);
                }
                errors++;
              }
            }
            if (data16.href !== void 0) {
              let data18 = data16.href;
              if (typeof data18 === "string") {
                if (!formats12(data18)) {
                  const err27 = { instancePath: instancePath + "/assets/" + i1 + "/href", schemaPath: "#/$defs/asset/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema49.properties.href, data: data18 };
                  if (vErrors === null) {
                    vErrors = [err27];
                  } else {
                    vErrors.push(err27);
                  }
                  errors++;
                }
              } else {
                const err28 = { instancePath: instancePath + "/assets/" + i1 + "/href", schemaPath: "#/$defs/asset/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.href.type, parentSchema: schema49.properties.href, data: data18 };
                if (vErrors === null) {
                  vErrors = [err28];
                } else {
                  vErrors.push(err28);
                }
                errors++;
              }
            }
            if (data16.label !== void 0) {
              let data19 = data16.label;
              if (typeof data19 !== "string") {
                const err29 = { instancePath: instancePath + "/assets/" + i1 + "/label", schemaPath: "#/$defs/asset/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.label.type, parentSchema: schema49.properties.label, data: data19 };
                if (vErrors === null) {
                  vErrors = [err29];
                } else {
                  vErrors.push(err29);
                }
                errors++;
              }
            }
            if (data16.description !== void 0) {
              let data20 = data16.description;
              if (typeof data20 !== "string") {
                const err30 = { instancePath: instancePath + "/assets/" + i1 + "/description", schemaPath: "#/$defs/asset/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.description.type, parentSchema: schema49.properties.description, data: data20 };
                if (vErrors === null) {
                  vErrors = [err30];
                } else {
                  vErrors.push(err30);
                }
                errors++;
              }
            }
            var props1 = {};
            props1.type = true;
            props1.href = true;
            props1.label = true;
            props1.description = true;
            for (const key3 in data16) {
              if (pattern10.test(key3)) {
                props1[key3] = true;
              }
            }
            if (props1 !== true) {
              for (const key4 in data16) {
                if (!props1 || !props1[key4]) {
                  const err31 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key4 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema49, data: data16 };
                  if (vErrors === null) {
                    vErrors = [err31];
                  } else {
                    vErrors.push(err31);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err32 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema49.type, parentSchema: schema49, data: data16 };
            if (vErrors === null) {
              vErrors = [err32];
            } else {
              vErrors.push(err32);
            }
            errors++;
          }
        }
      } else {
        const err33 = { instancePath: instancePath + "/assets", schemaPath: "#/properties/assets/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema50.properties.assets.type, parentSchema: schema50.properties.assets, data: data15 };
        if (vErrors === null) {
          vErrors = [err33];
        } else {
          vErrors.push(err33);
        }
        errors++;
      }
    }
    var props2 = {};
    props2.id = true;
    props2.name = true;
    props2.description = true;
    props2.type = true;
    props2.parent = true;
    props2.occurredAt = true;
    props2.participants = true;
    props2.attributes = true;
    props2.links = true;
    props2.assets = true;
    for (const key5 in data) {
      if (pattern10.test(key5)) {
        props2[key5] = true;
      }
    }
    if (props2 !== true) {
      for (const key6 in data) {
        if (!props2 || !props2[key6]) {
          const err34 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key6 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema50, data };
          if (vErrors === null) {
            vErrors = [err34];
          } else {
            vErrors.push(err34);
          }
          errors++;
        }
      }
    }
  } else {
    const err35 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema50.type, parentSchema: schema50, data };
    if (vErrors === null) {
      vErrors = [err35];
    } else {
      vErrors.push(err35);
    }
    errors++;
  }
  validate31.errors = vErrors;
  return errors === 0;
}
validate31.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema56 = { "type": "object", "required": ["participant"], "properties": { "participant": { "$ref": "#/$defs/identifier" }, "event": { "$ref": "#/$defs/identifier" }, "status": { "$ref": "#/$defs/status" }, "ranks": { "type": "object", "description": "Positions the producer published, keyed by ranking id. Informative: the ordering must be derivable without it (spec §7.5).", "propertyNames": { "$ref": "#/$defs/identifier" }, "additionalProperties": { "type": "integer", "minimum": 1 } }, "values": { "type": "object", "description": "Keys reference declared measure ids. null is forbidden: omit an unavailable measure (spec §7.3.2).", "additionalProperties": { "type": ["number", "string", "boolean"] } }, "attributes": { "$ref": "#/$defs/attributeValues" }, "notes": { "type": "string" }, "links": { "type": "array", "items": { "$ref": "#/$defs/link" } }, "assets": { "type": "array", "items": { "$ref": "#/$defs/asset" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
const schema59 = { "type": "string", "enum": ["finished", "bye", "notClassified", "inProgress", "dnf", "dns", "dsq", "outOfTime", "withdrawn"], "default": "finished" };
function validate35(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate35.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.participant === void 0) {
      const err0 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "participant" }, message: "must have required property 'participant'", schema: schema56.required, parentSchema: schema56, data };
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    }
    if (data.participant !== void 0) {
      let data0 = data.participant;
      if (typeof data0 === "string") {
        if (func1(data0) < 1) {
          const err1 = { instancePath: instancePath + "/participant", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err1];
          } else {
            vErrors.push(err1);
          }
          errors++;
        }
        if (!pattern5.test(data0)) {
          const err2 = { instancePath: instancePath + "/participant", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err2];
          } else {
            vErrors.push(err2);
          }
          errors++;
        }
      } else {
        const err3 = { instancePath: instancePath + "/participant", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data0 };
        if (vErrors === null) {
          vErrors = [err3];
        } else {
          vErrors.push(err3);
        }
        errors++;
      }
    }
    if (data.event !== void 0) {
      let data1 = data.event;
      if (typeof data1 === "string") {
        if (func1(data1) < 1) {
          const err4 = { instancePath: instancePath + "/event", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err4];
          } else {
            vErrors.push(err4);
          }
          errors++;
        }
        if (!pattern5.test(data1)) {
          const err5 = { instancePath: instancePath + "/event", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err5];
          } else {
            vErrors.push(err5);
          }
          errors++;
        }
      } else {
        const err6 = { instancePath: instancePath + "/event", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data1 };
        if (vErrors === null) {
          vErrors = [err6];
        } else {
          vErrors.push(err6);
        }
        errors++;
      }
    }
    if (data.status !== void 0) {
      let data2 = data.status;
      if (typeof data2 !== "string") {
        const err7 = { instancePath: instancePath + "/status", schemaPath: "#/$defs/status/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema59.type, parentSchema: schema59, data: data2 };
        if (vErrors === null) {
          vErrors = [err7];
        } else {
          vErrors.push(err7);
        }
        errors++;
      }
      if (!(data2 === "finished" || data2 === "bye" || data2 === "notClassified" || data2 === "inProgress" || data2 === "dnf" || data2 === "dns" || data2 === "dsq" || data2 === "outOfTime" || data2 === "withdrawn")) {
        const err8 = { instancePath: instancePath + "/status", schemaPath: "#/$defs/status/enum", keyword: "enum", params: { allowedValues: schema59.enum }, message: "must be equal to one of the allowed values", schema: schema59.enum, parentSchema: schema59, data: data2 };
        if (vErrors === null) {
          vErrors = [err8];
        } else {
          vErrors.push(err8);
        }
        errors++;
      }
    }
    if (data.ranks !== void 0) {
      let data3 = data.ranks;
      if (data3 && typeof data3 == "object" && !Array.isArray(data3)) {
        for (const key0 in data3) {
          const _errs12 = errors;
          if (typeof key0 === "string") {
            if (func1(key0) < 1) {
              const err9 = { instancePath: instancePath + "/ranks", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: key0, propertyName: key0 };
              if (vErrors === null) {
                vErrors = [err9];
              } else {
                vErrors.push(err9);
              }
              errors++;
            }
            if (!pattern5.test(key0)) {
              const err10 = { instancePath: instancePath + "/ranks", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: key0, propertyName: key0 };
              if (vErrors === null) {
                vErrors = [err10];
              } else {
                vErrors.push(err10);
              }
              errors++;
            }
          } else {
            const err11 = { instancePath: instancePath + "/ranks", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: key0, propertyName: key0 };
            if (vErrors === null) {
              vErrors = [err11];
            } else {
              vErrors.push(err11);
            }
            errors++;
          }
          var valid4 = _errs12 === errors;
          if (!valid4) {
            const err12 = { instancePath: instancePath + "/ranks", schemaPath: "#/properties/ranks/propertyNames", keyword: "propertyNames", params: { propertyName: key0 }, message: "property name must be valid", schema: schema56.properties.ranks.propertyNames, parentSchema: schema56.properties.ranks, data: data3 };
            if (vErrors === null) {
              vErrors = [err12];
            } else {
              vErrors.push(err12);
            }
            errors++;
          }
        }
        for (const key1 in data3) {
          let data4 = data3[key1];
          if (!(typeof data4 == "number" && (!(data4 % 1) && !isNaN(data4)))) {
            const err13 = { instancePath: instancePath + "/ranks/" + key1.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/properties/ranks/additionalProperties/type", keyword: "type", params: { type: "integer" }, message: "must be integer", schema: schema56.properties.ranks.additionalProperties.type, parentSchema: schema56.properties.ranks.additionalProperties, data: data4 };
            if (vErrors === null) {
              vErrors = [err13];
            } else {
              vErrors.push(err13);
            }
            errors++;
          }
          if (typeof data4 == "number") {
            if (data4 < 1 || isNaN(data4)) {
              const err14 = { instancePath: instancePath + "/ranks/" + key1.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/properties/ranks/additionalProperties/minimum", keyword: "minimum", params: { comparison: ">=", limit: 1 }, message: "must be >= 1", schema: 1, parentSchema: schema56.properties.ranks.additionalProperties, data: data4 };
              if (vErrors === null) {
                vErrors = [err14];
              } else {
                vErrors.push(err14);
              }
              errors++;
            }
          }
        }
      } else {
        const err15 = { instancePath: instancePath + "/ranks", schemaPath: "#/properties/ranks/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema56.properties.ranks.type, parentSchema: schema56.properties.ranks, data: data3 };
        if (vErrors === null) {
          vErrors = [err15];
        } else {
          vErrors.push(err15);
        }
        errors++;
      }
    }
    if (data.values !== void 0) {
      let data5 = data.values;
      if (data5 && typeof data5 == "object" && !Array.isArray(data5)) {
        for (const key2 in data5) {
          let data6 = data5[key2];
          if (!(typeof data6 == "number") && typeof data6 !== "string" && typeof data6 !== "boolean") {
            const err16 = { instancePath: instancePath + "/values/" + key2.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/properties/values/additionalProperties/type", keyword: "type", params: { type: schema56.properties.values.additionalProperties.type }, message: "must be number,string,boolean", schema: schema56.properties.values.additionalProperties.type, parentSchema: schema56.properties.values.additionalProperties, data: data6 };
            if (vErrors === null) {
              vErrors = [err16];
            } else {
              vErrors.push(err16);
            }
            errors++;
          }
        }
      } else {
        const err17 = { instancePath: instancePath + "/values", schemaPath: "#/properties/values/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema56.properties.values.type, parentSchema: schema56.properties.values, data: data5 };
        if (vErrors === null) {
          vErrors = [err17];
        } else {
          vErrors.push(err17);
        }
        errors++;
      }
    }
    if (data.attributes !== void 0) {
      let data7 = data.attributes;
      if (data7 && typeof data7 == "object" && !Array.isArray(data7)) {
        for (const key3 in data7) {
          let data8 = data7[key3];
          if (typeof data8 !== "string" && !(typeof data8 == "number") && typeof data8 !== "boolean") {
            const err18 = { instancePath: instancePath + "/attributes/" + key3.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/$defs/attributeValues/additionalProperties/type", keyword: "type", params: { type: schema42.additionalProperties.type }, message: "must be string,number,boolean", schema: schema42.additionalProperties.type, parentSchema: schema42.additionalProperties, data: data8 };
            if (vErrors === null) {
              vErrors = [err18];
            } else {
              vErrors.push(err18);
            }
            errors++;
          }
        }
      } else {
        const err19 = { instancePath: instancePath + "/attributes", schemaPath: "#/$defs/attributeValues/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema42.type, parentSchema: schema42, data: data7 };
        if (vErrors === null) {
          vErrors = [err19];
        } else {
          vErrors.push(err19);
        }
        errors++;
      }
    }
    if (data.notes !== void 0) {
      let data9 = data.notes;
      if (typeof data9 !== "string") {
        const err20 = { instancePath: instancePath + "/notes", schemaPath: "#/properties/notes/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema56.properties.notes.type, parentSchema: schema56.properties.notes, data: data9 };
        if (vErrors === null) {
          vErrors = [err20];
        } else {
          vErrors.push(err20);
        }
        errors++;
      }
    }
    if (data.links !== void 0) {
      let data10 = data.links;
      if (Array.isArray(data10)) {
        const len0 = data10.length;
        for (let i0 = 0; i0 < len0; i0++) {
          let data11 = data10[i0];
          if (data11 && typeof data11 == "object" && !Array.isArray(data11)) {
            if (data11.href === void 0) {
              const err21 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema48.required, parentSchema: schema48, data: data11 };
              if (vErrors === null) {
                vErrors = [err21];
              } else {
                vErrors.push(err21);
              }
              errors++;
            }
            if (data11.rel !== void 0) {
              let data12 = data11.rel;
              if (typeof data12 !== "string") {
                const err22 = { instancePath: instancePath + "/links/" + i0 + "/rel", schemaPath: "#/$defs/link/properties/rel/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.rel.type, parentSchema: schema48.properties.rel, data: data12 };
                if (vErrors === null) {
                  vErrors = [err22];
                } else {
                  vErrors.push(err22);
                }
                errors++;
              }
            }
            if (data11.href !== void 0) {
              let data13 = data11.href;
              if (typeof data13 === "string") {
                if (!formats12(data13)) {
                  const err23 = { instancePath: instancePath + "/links/" + i0 + "/href", schemaPath: "#/$defs/link/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema48.properties.href, data: data13 };
                  if (vErrors === null) {
                    vErrors = [err23];
                  } else {
                    vErrors.push(err23);
                  }
                  errors++;
                }
              } else {
                const err24 = { instancePath: instancePath + "/links/" + i0 + "/href", schemaPath: "#/$defs/link/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.href.type, parentSchema: schema48.properties.href, data: data13 };
                if (vErrors === null) {
                  vErrors = [err24];
                } else {
                  vErrors.push(err24);
                }
                errors++;
              }
            }
            if (data11.label !== void 0) {
              let data14 = data11.label;
              if (typeof data14 !== "string") {
                const err25 = { instancePath: instancePath + "/links/" + i0 + "/label", schemaPath: "#/$defs/link/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.label.type, parentSchema: schema48.properties.label, data: data14 };
                if (vErrors === null) {
                  vErrors = [err25];
                } else {
                  vErrors.push(err25);
                }
                errors++;
              }
            }
            if (data11.description !== void 0) {
              let data15 = data11.description;
              if (typeof data15 !== "string") {
                const err26 = { instancePath: instancePath + "/links/" + i0 + "/description", schemaPath: "#/$defs/link/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.description.type, parentSchema: schema48.properties.description, data: data15 };
                if (vErrors === null) {
                  vErrors = [err26];
                } else {
                  vErrors.push(err26);
                }
                errors++;
              }
            }
            var props0 = {};
            props0.rel = true;
            props0.href = true;
            props0.label = true;
            props0.description = true;
            for (const key4 in data11) {
              if (pattern10.test(key4)) {
                props0[key4] = true;
              }
            }
            if (props0 !== true) {
              for (const key5 in data11) {
                if (!props0 || !props0[key5]) {
                  const err27 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key5 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema48, data: data11 };
                  if (vErrors === null) {
                    vErrors = [err27];
                  } else {
                    vErrors.push(err27);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err28 = { instancePath: instancePath + "/links/" + i0, schemaPath: "#/$defs/link/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema48.type, parentSchema: schema48, data: data11 };
            if (vErrors === null) {
              vErrors = [err28];
            } else {
              vErrors.push(err28);
            }
            errors++;
          }
        }
      } else {
        const err29 = { instancePath: instancePath + "/links", schemaPath: "#/properties/links/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema56.properties.links.type, parentSchema: schema56.properties.links, data: data10 };
        if (vErrors === null) {
          vErrors = [err29];
        } else {
          vErrors.push(err29);
        }
        errors++;
      }
    }
    if (data.assets !== void 0) {
      let data16 = data.assets;
      if (Array.isArray(data16)) {
        const len1 = data16.length;
        for (let i1 = 0; i1 < len1; i1++) {
          let data17 = data16[i1];
          if (data17 && typeof data17 == "object" && !Array.isArray(data17)) {
            if (data17.href === void 0) {
              const err30 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema49.required, parentSchema: schema49, data: data17 };
              if (vErrors === null) {
                vErrors = [err30];
              } else {
                vErrors.push(err30);
              }
              errors++;
            }
            if (data17.type !== void 0) {
              let data18 = data17.type;
              if (typeof data18 !== "string") {
                const err31 = { instancePath: instancePath + "/assets/" + i1 + "/type", schemaPath: "#/$defs/asset/properties/type/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.type.type, parentSchema: schema49.properties.type, data: data18 };
                if (vErrors === null) {
                  vErrors = [err31];
                } else {
                  vErrors.push(err31);
                }
                errors++;
              }
              if (!(data18 === "image" || data18 === "video" || data18 === "audio" || data18 === "document" || data18 === "other")) {
                const err32 = { instancePath: instancePath + "/assets/" + i1 + "/type", schemaPath: "#/$defs/asset/properties/type/enum", keyword: "enum", params: { allowedValues: schema49.properties.type.enum }, message: "must be equal to one of the allowed values", schema: schema49.properties.type.enum, parentSchema: schema49.properties.type, data: data18 };
                if (vErrors === null) {
                  vErrors = [err32];
                } else {
                  vErrors.push(err32);
                }
                errors++;
              }
            }
            if (data17.href !== void 0) {
              let data19 = data17.href;
              if (typeof data19 === "string") {
                if (!formats12(data19)) {
                  const err33 = { instancePath: instancePath + "/assets/" + i1 + "/href", schemaPath: "#/$defs/asset/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema49.properties.href, data: data19 };
                  if (vErrors === null) {
                    vErrors = [err33];
                  } else {
                    vErrors.push(err33);
                  }
                  errors++;
                }
              } else {
                const err34 = { instancePath: instancePath + "/assets/" + i1 + "/href", schemaPath: "#/$defs/asset/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.href.type, parentSchema: schema49.properties.href, data: data19 };
                if (vErrors === null) {
                  vErrors = [err34];
                } else {
                  vErrors.push(err34);
                }
                errors++;
              }
            }
            if (data17.label !== void 0) {
              let data20 = data17.label;
              if (typeof data20 !== "string") {
                const err35 = { instancePath: instancePath + "/assets/" + i1 + "/label", schemaPath: "#/$defs/asset/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.label.type, parentSchema: schema49.properties.label, data: data20 };
                if (vErrors === null) {
                  vErrors = [err35];
                } else {
                  vErrors.push(err35);
                }
                errors++;
              }
            }
            if (data17.description !== void 0) {
              let data21 = data17.description;
              if (typeof data21 !== "string") {
                const err36 = { instancePath: instancePath + "/assets/" + i1 + "/description", schemaPath: "#/$defs/asset/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.description.type, parentSchema: schema49.properties.description, data: data21 };
                if (vErrors === null) {
                  vErrors = [err36];
                } else {
                  vErrors.push(err36);
                }
                errors++;
              }
            }
            var props1 = {};
            props1.type = true;
            props1.href = true;
            props1.label = true;
            props1.description = true;
            for (const key6 in data17) {
              if (pattern10.test(key6)) {
                props1[key6] = true;
              }
            }
            if (props1 !== true) {
              for (const key7 in data17) {
                if (!props1 || !props1[key7]) {
                  const err37 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key7 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema49, data: data17 };
                  if (vErrors === null) {
                    vErrors = [err37];
                  } else {
                    vErrors.push(err37);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err38 = { instancePath: instancePath + "/assets/" + i1, schemaPath: "#/$defs/asset/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema49.type, parentSchema: schema49, data: data17 };
            if (vErrors === null) {
              vErrors = [err38];
            } else {
              vErrors.push(err38);
            }
            errors++;
          }
        }
      } else {
        const err39 = { instancePath: instancePath + "/assets", schemaPath: "#/properties/assets/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema56.properties.assets.type, parentSchema: schema56.properties.assets, data: data16 };
        if (vErrors === null) {
          vErrors = [err39];
        } else {
          vErrors.push(err39);
        }
        errors++;
      }
    }
    var props2 = {};
    props2.participant = true;
    props2.event = true;
    props2.status = true;
    props2.ranks = true;
    props2.values = true;
    props2.attributes = true;
    props2.notes = true;
    props2.links = true;
    props2.assets = true;
    for (const key8 in data) {
      if (pattern10.test(key8)) {
        props2[key8] = true;
      }
    }
    if (props2 !== true) {
      for (const key9 in data) {
        if (!props2 || !props2[key9]) {
          const err40 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key9 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema56, data };
          if (vErrors === null) {
            vErrors = [err40];
          } else {
            vErrors.push(err40);
          }
          errors++;
        }
      }
    }
  } else {
    const err41 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema56.type, parentSchema: schema56, data };
    if (vErrors === null) {
      vErrors = [err41];
    } else {
      vErrors.push(err41);
    }
    errors++;
  }
  validate35.errors = vErrors;
  return errors === 0;
}
validate35.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema64 = { "type": "object", "required": ["id", "label", "sortBy"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "scope": { "type": "object", "properties": { "event": { "oneOf": [{ "$ref": "#/$defs/identifier" }, { "$ref": "#/$defs/identifierList" }] }, "category": { "oneOf": [{ "$ref": "#/$defs/identifier" }, { "$ref": "#/$defs/identifierList" }] } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false }, "sortBy": { "type": "array", "items": { "$ref": "#/$defs/identifier" }, "description": "Measure ids by decreasing priority. Direction comes from the measure (spec §8.2.3)." }, "ties": { "type": "string", "enum": ["standard", "dense", "strict", "resolved"], "default": "standard" }, "excludeStatuses": { "type": "array", "items": { "$ref": "#/$defs/status" } } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false, "allOf": [{ "description": "sortBy may only be empty when ties is resolved (spec §8.2.1).", "if": { "properties": { "ties": { "const": "resolved" } }, "required": ["ties"] }, "else": { "properties": { "sortBy": { "minItems": 1 } } } }] };
function validate37(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate37.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  const _errs2 = errors;
  let valid1 = true;
  const _errs3 = errors;
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.ties === void 0 && "ties") {
      const err0 = {};
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    } else {
      if (data.ties !== void 0) {
        if ("resolved" !== data.ties) {
          const err1 = {};
          if (vErrors === null) {
            vErrors = [err1];
          } else {
            vErrors.push(err1);
          }
          errors++;
        }
      }
    }
  }
  var _valid0 = _errs3 === errors;
  errors = _errs2;
  if (vErrors !== null) {
    if (_errs2) {
      vErrors.length = _errs2;
    } else {
      vErrors = null;
    }
  }
  if (!_valid0) {
    const _errs5 = errors;
    if (data && typeof data == "object" && !Array.isArray(data)) {
      if (data.sortBy !== void 0) {
        let data1 = data.sortBy;
        if (Array.isArray(data1)) {
          if (data1.length < 1) {
            const err2 = { instancePath: instancePath + "/sortBy", schemaPath: "#/allOf/0/else/properties/sortBy/minItems", keyword: "minItems", params: { limit: 1 }, message: "must NOT have fewer than 1 items", schema: 1, parentSchema: schema64.allOf[0].else.properties.sortBy, data: data1 };
            if (vErrors === null) {
              vErrors = [err2];
            } else {
              vErrors.push(err2);
            }
            errors++;
          }
        }
      }
    }
    var _valid0 = _errs5 === errors;
    valid1 = _valid0;
    if (valid1) {
      var props0 = {};
      props0.sortBy = true;
      props0.ties = true;
    }
  }
  if (!valid1) {
    const err3 = { instancePath, schemaPath: "#/allOf/0/if", keyword: "if", params: { failingKeyword: "else" }, message: 'must match "else" schema', schema: schema64.allOf[0].if, parentSchema: schema64.allOf[0], data };
    if (vErrors === null) {
      vErrors = [err3];
    } else {
      vErrors.push(err3);
    }
    errors++;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.id === void 0) {
      const err4 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "id" }, message: "must have required property 'id'", schema: schema64.required, parentSchema: schema64, data };
      if (vErrors === null) {
        vErrors = [err4];
      } else {
        vErrors.push(err4);
      }
      errors++;
    }
    if (data.label === void 0) {
      const err5 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "label" }, message: "must have required property 'label'", schema: schema64.required, parentSchema: schema64, data };
      if (vErrors === null) {
        vErrors = [err5];
      } else {
        vErrors.push(err5);
      }
      errors++;
    }
    if (data.sortBy === void 0) {
      const err6 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "sortBy" }, message: "must have required property 'sortBy'", schema: schema64.required, parentSchema: schema64, data };
      if (vErrors === null) {
        vErrors = [err6];
      } else {
        vErrors.push(err6);
      }
      errors++;
    }
    if (props0 !== true) {
      props0 = props0 || {};
      props0.id = true;
      props0.label = true;
      props0.description = true;
      props0.scope = true;
      props0.sortBy = true;
      props0.ties = true;
      props0.excludeStatuses = true;
    }
    if (data.id !== void 0) {
      let data2 = data.id;
      if (typeof data2 === "string") {
        if (func1(data2) < 1) {
          const err7 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data2 };
          if (vErrors === null) {
            vErrors = [err7];
          } else {
            vErrors.push(err7);
          }
          errors++;
        }
        if (!pattern5.test(data2)) {
          const err8 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data2 };
          if (vErrors === null) {
            vErrors = [err8];
          } else {
            vErrors.push(err8);
          }
          errors++;
        }
      } else {
        const err9 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data2 };
        if (vErrors === null) {
          vErrors = [err9];
        } else {
          vErrors.push(err9);
        }
        errors++;
      }
    }
    if (data.label !== void 0) {
      let data3 = data.label;
      if (typeof data3 === "string") {
        if (func1(data3) < 1) {
          const err10 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema64.properties.label, data: data3 };
          if (vErrors === null) {
            vErrors = [err10];
          } else {
            vErrors.push(err10);
          }
          errors++;
        }
      } else {
        const err11 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema64.properties.label.type, parentSchema: schema64.properties.label, data: data3 };
        if (vErrors === null) {
          vErrors = [err11];
        } else {
          vErrors.push(err11);
        }
        errors++;
      }
    }
    if (data.description !== void 0) {
      let data4 = data.description;
      if (typeof data4 !== "string") {
        const err12 = { instancePath: instancePath + "/description", schemaPath: "#/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema64.properties.description.type, parentSchema: schema64.properties.description, data: data4 };
        if (vErrors === null) {
          vErrors = [err12];
        } else {
          vErrors.push(err12);
        }
        errors++;
      }
    }
    if (data.scope !== void 0) {
      let data5 = data.scope;
      if (data5 && typeof data5 == "object" && !Array.isArray(data5)) {
        if (data5.event !== void 0) {
          let data6 = data5.event;
          const _errs17 = errors;
          let valid7 = false;
          let passing0 = null;
          const _errs18 = errors;
          if (typeof data6 === "string") {
            if (func1(data6) < 1) {
              const err13 = { instancePath: instancePath + "/scope/event", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data6 };
              if (vErrors === null) {
                vErrors = [err13];
              } else {
                vErrors.push(err13);
              }
              errors++;
            }
            if (!pattern5.test(data6)) {
              const err14 = { instancePath: instancePath + "/scope/event", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data6 };
              if (vErrors === null) {
                vErrors = [err14];
              } else {
                vErrors.push(err14);
              }
              errors++;
            }
          } else {
            const err15 = { instancePath: instancePath + "/scope/event", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data6 };
            if (vErrors === null) {
              vErrors = [err15];
            } else {
              vErrors.push(err15);
            }
            errors++;
          }
          var _valid1 = _errs18 === errors;
          if (_valid1) {
            valid7 = true;
            passing0 = 0;
          }
          const _errs21 = errors;
          if (!validate28(data6, { instancePath: instancePath + "/scope/event", parentData: data5, parentDataProperty: "event", rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
            errors = vErrors.length;
          }
          var _valid1 = _errs21 === errors;
          if (_valid1 && valid7) {
            valid7 = false;
            passing0 = [passing0, 1];
          } else {
            if (_valid1) {
              valid7 = true;
              passing0 = 1;
            }
          }
          if (!valid7) {
            const err16 = { instancePath: instancePath + "/scope/event", schemaPath: "#/properties/scope/properties/event/oneOf", keyword: "oneOf", params: { passingSchemas: passing0 }, message: "must match exactly one schema in oneOf", schema: schema64.properties.scope.properties.event.oneOf, parentSchema: schema64.properties.scope.properties.event, data: data6 };
            if (vErrors === null) {
              vErrors = [err16];
            } else {
              vErrors.push(err16);
            }
            errors++;
          } else {
            errors = _errs17;
            if (vErrors !== null) {
              if (_errs17) {
                vErrors.length = _errs17;
              } else {
                vErrors = null;
              }
            }
          }
        }
        if (data5.category !== void 0) {
          let data7 = data5.category;
          const _errs23 = errors;
          let valid9 = false;
          let passing1 = null;
          const _errs24 = errors;
          if (typeof data7 === "string") {
            if (func1(data7) < 1) {
              const err17 = { instancePath: instancePath + "/scope/category", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data7 };
              if (vErrors === null) {
                vErrors = [err17];
              } else {
                vErrors.push(err17);
              }
              errors++;
            }
            if (!pattern5.test(data7)) {
              const err18 = { instancePath: instancePath + "/scope/category", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data7 };
              if (vErrors === null) {
                vErrors = [err18];
              } else {
                vErrors.push(err18);
              }
              errors++;
            }
          } else {
            const err19 = { instancePath: instancePath + "/scope/category", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data7 };
            if (vErrors === null) {
              vErrors = [err19];
            } else {
              vErrors.push(err19);
            }
            errors++;
          }
          var _valid2 = _errs24 === errors;
          if (_valid2) {
            valid9 = true;
            passing1 = 0;
          }
          const _errs27 = errors;
          if (!validate28(data7, { instancePath: instancePath + "/scope/category", parentData: data5, parentDataProperty: "category", rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
            errors = vErrors.length;
          }
          var _valid2 = _errs27 === errors;
          if (_valid2 && valid9) {
            valid9 = false;
            passing1 = [passing1, 1];
          } else {
            if (_valid2) {
              valid9 = true;
              passing1 = 1;
            }
          }
          if (!valid9) {
            const err20 = { instancePath: instancePath + "/scope/category", schemaPath: "#/properties/scope/properties/category/oneOf", keyword: "oneOf", params: { passingSchemas: passing1 }, message: "must match exactly one schema in oneOf", schema: schema64.properties.scope.properties.category.oneOf, parentSchema: schema64.properties.scope.properties.category, data: data7 };
            if (vErrors === null) {
              vErrors = [err20];
            } else {
              vErrors.push(err20);
            }
            errors++;
          } else {
            errors = _errs23;
            if (vErrors !== null) {
              if (_errs23) {
                vErrors.length = _errs23;
              } else {
                vErrors = null;
              }
            }
          }
        }
        var props1 = {};
        props1.event = true;
        props1.category = true;
        for (const key0 in data5) {
          if (pattern10.test(key0)) {
            props1[key0] = true;
          }
        }
        if (props1 !== true) {
          for (const key1 in data5) {
            if (!props1 || !props1[key1]) {
              const err21 = { instancePath: instancePath + "/scope", schemaPath: "#/properties/scope/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key1 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema64.properties.scope, data: data5 };
              if (vErrors === null) {
                vErrors = [err21];
              } else {
                vErrors.push(err21);
              }
              errors++;
            }
          }
        }
      } else {
        const err22 = { instancePath: instancePath + "/scope", schemaPath: "#/properties/scope/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema64.properties.scope.type, parentSchema: schema64.properties.scope, data: data5 };
        if (vErrors === null) {
          vErrors = [err22];
        } else {
          vErrors.push(err22);
        }
        errors++;
      }
    }
    if (data.sortBy !== void 0) {
      let data8 = data.sortBy;
      if (Array.isArray(data8)) {
        const len0 = data8.length;
        for (let i0 = 0; i0 < len0; i0++) {
          let data9 = data8[i0];
          if (typeof data9 === "string") {
            if (func1(data9) < 1) {
              const err23 = { instancePath: instancePath + "/sortBy/" + i0, schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data9 };
              if (vErrors === null) {
                vErrors = [err23];
              } else {
                vErrors.push(err23);
              }
              errors++;
            }
            if (!pattern5.test(data9)) {
              const err24 = { instancePath: instancePath + "/sortBy/" + i0, schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data9 };
              if (vErrors === null) {
                vErrors = [err24];
              } else {
                vErrors.push(err24);
              }
              errors++;
            }
          } else {
            const err25 = { instancePath: instancePath + "/sortBy/" + i0, schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data9 };
            if (vErrors === null) {
              vErrors = [err25];
            } else {
              vErrors.push(err25);
            }
            errors++;
          }
        }
      } else {
        const err26 = { instancePath: instancePath + "/sortBy", schemaPath: "#/properties/sortBy/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema64.properties.sortBy.type, parentSchema: schema64.properties.sortBy, data: data8 };
        if (vErrors === null) {
          vErrors = [err26];
        } else {
          vErrors.push(err26);
        }
        errors++;
      }
    }
    if (data.ties !== void 0) {
      let data10 = data.ties;
      if (typeof data10 !== "string") {
        const err27 = { instancePath: instancePath + "/ties", schemaPath: "#/properties/ties/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema64.properties.ties.type, parentSchema: schema64.properties.ties, data: data10 };
        if (vErrors === null) {
          vErrors = [err27];
        } else {
          vErrors.push(err27);
        }
        errors++;
      }
      if (!(data10 === "standard" || data10 === "dense" || data10 === "strict" || data10 === "resolved")) {
        const err28 = { instancePath: instancePath + "/ties", schemaPath: "#/properties/ties/enum", keyword: "enum", params: { allowedValues: schema64.properties.ties.enum }, message: "must be equal to one of the allowed values", schema: schema64.properties.ties.enum, parentSchema: schema64.properties.ties, data: data10 };
        if (vErrors === null) {
          vErrors = [err28];
        } else {
          vErrors.push(err28);
        }
        errors++;
      }
    }
    if (data.excludeStatuses !== void 0) {
      let data11 = data.excludeStatuses;
      if (Array.isArray(data11)) {
        const len1 = data11.length;
        for (let i1 = 0; i1 < len1; i1++) {
          let data12 = data11[i1];
          if (typeof data12 !== "string") {
            const err29 = { instancePath: instancePath + "/excludeStatuses/" + i1, schemaPath: "#/$defs/status/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema59.type, parentSchema: schema59, data: data12 };
            if (vErrors === null) {
              vErrors = [err29];
            } else {
              vErrors.push(err29);
            }
            errors++;
          }
          if (!(data12 === "finished" || data12 === "bye" || data12 === "notClassified" || data12 === "inProgress" || data12 === "dnf" || data12 === "dns" || data12 === "dsq" || data12 === "outOfTime" || data12 === "withdrawn")) {
            const err30 = { instancePath: instancePath + "/excludeStatuses/" + i1, schemaPath: "#/$defs/status/enum", keyword: "enum", params: { allowedValues: schema59.enum }, message: "must be equal to one of the allowed values", schema: schema59.enum, parentSchema: schema59, data: data12 };
            if (vErrors === null) {
              vErrors = [err30];
            } else {
              vErrors.push(err30);
            }
            errors++;
          }
        }
      } else {
        const err31 = { instancePath: instancePath + "/excludeStatuses", schemaPath: "#/properties/excludeStatuses/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema64.properties.excludeStatuses.type, parentSchema: schema64.properties.excludeStatuses, data: data11 };
        if (vErrors === null) {
          vErrors = [err31];
        } else {
          vErrors.push(err31);
        }
        errors++;
      }
    }
    for (const key2 in data) {
      if (pattern10.test(key2)) {
        props0[key2] = true;
      }
    }
    if (props0 !== true) {
      for (const key3 in data) {
        if (!props0 || !props0[key3]) {
          const err32 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key3 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema64, data };
          if (vErrors === null) {
            vErrors = [err32];
          } else {
            vErrors.push(err32);
          }
          errors++;
        }
      }
    }
  } else {
    const err33 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema64.type, parentSchema: schema64, data };
    if (vErrors === null) {
      vErrors = [err33];
    } else {
      vErrors.push(err33);
    }
    errors++;
  }
  validate37.errors = vErrors;
  return errors === 0;
}
validate37.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema70 = { "type": "object", "required": ["id", "label"], "properties": { "id": { "$ref": "#/$defs/identifier" }, "label": { "type": "string", "minLength": 1 }, "description": { "type": "string" }, "attributes": { "$ref": "#/$defs/attributeValues" }, "participants": { "$ref": "#/$defs/identifierList" }, "parent": { "$ref": "#/$defs/identifier" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
function validate41(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate41.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.id === void 0) {
      const err0 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "id" }, message: "must have required property 'id'", schema: schema70.required, parentSchema: schema70, data };
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    }
    if (data.label === void 0) {
      const err1 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "label" }, message: "must have required property 'label'", schema: schema70.required, parentSchema: schema70, data };
      if (vErrors === null) {
        vErrors = [err1];
      } else {
        vErrors.push(err1);
      }
      errors++;
    }
    if (data.id !== void 0) {
      let data0 = data.id;
      if (typeof data0 === "string") {
        if (func1(data0) < 1) {
          const err2 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err2];
          } else {
            vErrors.push(err2);
          }
          errors++;
        }
        if (!pattern5.test(data0)) {
          const err3 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data0 };
          if (vErrors === null) {
            vErrors = [err3];
          } else {
            vErrors.push(err3);
          }
          errors++;
        }
      } else {
        const err4 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data0 };
        if (vErrors === null) {
          vErrors = [err4];
        } else {
          vErrors.push(err4);
        }
        errors++;
      }
    }
    if (data.label !== void 0) {
      let data1 = data.label;
      if (typeof data1 === "string") {
        if (func1(data1) < 1) {
          const err5 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema70.properties.label, data: data1 };
          if (vErrors === null) {
            vErrors = [err5];
          } else {
            vErrors.push(err5);
          }
          errors++;
        }
      } else {
        const err6 = { instancePath: instancePath + "/label", schemaPath: "#/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema70.properties.label.type, parentSchema: schema70.properties.label, data: data1 };
        if (vErrors === null) {
          vErrors = [err6];
        } else {
          vErrors.push(err6);
        }
        errors++;
      }
    }
    if (data.description !== void 0) {
      let data2 = data.description;
      if (typeof data2 !== "string") {
        const err7 = { instancePath: instancePath + "/description", schemaPath: "#/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema70.properties.description.type, parentSchema: schema70.properties.description, data: data2 };
        if (vErrors === null) {
          vErrors = [err7];
        } else {
          vErrors.push(err7);
        }
        errors++;
      }
    }
    if (data.attributes !== void 0) {
      let data3 = data.attributes;
      if (data3 && typeof data3 == "object" && !Array.isArray(data3)) {
        for (const key0 in data3) {
          let data4 = data3[key0];
          if (typeof data4 !== "string" && !(typeof data4 == "number") && typeof data4 !== "boolean") {
            const err8 = { instancePath: instancePath + "/attributes/" + key0.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/$defs/attributeValues/additionalProperties/type", keyword: "type", params: { type: schema42.additionalProperties.type }, message: "must be string,number,boolean", schema: schema42.additionalProperties.type, parentSchema: schema42.additionalProperties, data: data4 };
            if (vErrors === null) {
              vErrors = [err8];
            } else {
              vErrors.push(err8);
            }
            errors++;
          }
        }
      } else {
        const err9 = { instancePath: instancePath + "/attributes", schemaPath: "#/$defs/attributeValues/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema42.type, parentSchema: schema42, data: data3 };
        if (vErrors === null) {
          vErrors = [err9];
        } else {
          vErrors.push(err9);
        }
        errors++;
      }
    }
    if (data.participants !== void 0) {
      if (!validate28(data.participants, { instancePath: instancePath + "/participants", parentData: data, parentDataProperty: "participants", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
        errors = vErrors.length;
      }
    }
    if (data.parent !== void 0) {
      let data6 = data.parent;
      if (typeof data6 === "string") {
        if (func1(data6) < 1) {
          const err10 = { instancePath: instancePath + "/parent", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data6 };
          if (vErrors === null) {
            vErrors = [err10];
          } else {
            vErrors.push(err10);
          }
          errors++;
        }
        if (!pattern5.test(data6)) {
          const err11 = { instancePath: instancePath + "/parent", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data6 };
          if (vErrors === null) {
            vErrors = [err11];
          } else {
            vErrors.push(err11);
          }
          errors++;
        }
      } else {
        const err12 = { instancePath: instancePath + "/parent", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data6 };
        if (vErrors === null) {
          vErrors = [err12];
        } else {
          vErrors.push(err12);
        }
        errors++;
      }
    }
    var props0 = {};
    props0.id = true;
    props0.label = true;
    props0.description = true;
    props0.attributes = true;
    props0.participants = true;
    props0.parent = true;
    for (const key1 in data) {
      if (pattern10.test(key1)) {
        props0[key1] = true;
      }
    }
    if (props0 !== true) {
      for (const key2 in data) {
        if (!props0 || !props0[key2]) {
          const err13 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key2 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema70, data };
          if (vErrors === null) {
            vErrors = [err13];
          } else {
            vErrors.push(err13);
          }
          errors++;
        }
      }
    }
  } else {
    const err14 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema70.type, parentSchema: schema70, data };
    if (vErrors === null) {
      vErrors = [err14];
    } else {
      vErrors.push(err14);
    }
    errors++;
  }
  validate41.errors = vErrors;
  return errors === 0;
}
validate41.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const schema76 = { "type": "object", "description": "Non-normative display hints. A consumer may ignore this object entirely (spec §10.1).", "properties": { "defaultView": { "type": "string" }, "measureOrder": { "$ref": "#/$defs/identifierList" }, "attributeOrder": { "$ref": "#/$defs/identifierList" }, "highlight": { "$ref": "#/$defs/identifierList" } }, "patternProperties": { "^x-": true }, "unevaluatedProperties": false };
function validate44(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate44.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.defaultView !== void 0) {
      let data0 = data.defaultView;
      if (typeof data0 !== "string") {
        const err0 = { instancePath: instancePath + "/defaultView", schemaPath: "#/properties/defaultView/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema76.properties.defaultView.type, parentSchema: schema76.properties.defaultView, data: data0 };
        if (vErrors === null) {
          vErrors = [err0];
        } else {
          vErrors.push(err0);
        }
        errors++;
      }
    }
    if (data.measureOrder !== void 0) {
      if (!validate28(data.measureOrder, { instancePath: instancePath + "/measureOrder", parentData: data, parentDataProperty: "measureOrder", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
        errors = vErrors.length;
      }
    }
    if (data.attributeOrder !== void 0) {
      if (!validate28(data.attributeOrder, { instancePath: instancePath + "/attributeOrder", parentData: data, parentDataProperty: "attributeOrder", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
        errors = vErrors.length;
      }
    }
    if (data.highlight !== void 0) {
      if (!validate28(data.highlight, { instancePath: instancePath + "/highlight", parentData: data, parentDataProperty: "highlight", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate28.errors : vErrors.concat(validate28.errors);
        errors = vErrors.length;
      }
    }
    var props0 = {};
    props0.defaultView = true;
    props0.measureOrder = true;
    props0.attributeOrder = true;
    props0.highlight = true;
    for (const key0 in data) {
      if (pattern10.test(key0)) {
        props0[key0] = true;
      }
    }
    if (props0 !== true) {
      for (const key1 in data) {
        if (!props0 || !props0[key1]) {
          const err1 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key1 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema76, data };
          if (vErrors === null) {
            vErrors = [err1];
          } else {
            vErrors.push(err1);
          }
          errors++;
        }
      }
    }
  } else {
    const err2 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema76.type, parentSchema: schema76, data };
    if (vErrors === null) {
      vErrors = [err2];
    } else {
      vErrors.push(err2);
    }
    errors++;
  }
  validate44.errors = vErrors;
  return errors === 0;
}
validate44.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
function validate20(data, { instancePath = "", parentData, parentDataProperty, rootData = data, dynamicAnchors = {} } = {}) {
  let vErrors = null;
  let errors = 0;
  const evaluated0 = validate20.evaluated;
  if (evaluated0.dynamicProps) {
    evaluated0.props = void 0;
  }
  if (evaluated0.dynamicItems) {
    evaluated0.items = void 0;
  }
  if (data && typeof data == "object" && !Array.isArray(data)) {
    if (data.openresult === void 0) {
      const err0 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "openresult" }, message: "must have required property 'openresult'", schema: schema31.required, parentSchema: schema31, data };
      if (vErrors === null) {
        vErrors = [err0];
      } else {
        vErrors.push(err0);
      }
      errors++;
    }
    if (data.title === void 0) {
      const err1 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "title" }, message: "must have required property 'title'", schema: schema31.required, parentSchema: schema31, data };
      if (vErrors === null) {
        vErrors = [err1];
      } else {
        vErrors.push(err1);
      }
      errors++;
    }
    if (data.participants === void 0) {
      const err2 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "participants" }, message: "must have required property 'participants'", schema: schema31.required, parentSchema: schema31, data };
      if (vErrors === null) {
        vErrors = [err2];
      } else {
        vErrors.push(err2);
      }
      errors++;
    }
    if (data.results === void 0) {
      const err3 = { instancePath, schemaPath: "#/required", keyword: "required", params: { missingProperty: "results" }, message: "must have required property 'results'", schema: schema31.required, parentSchema: schema31, data };
      if (vErrors === null) {
        vErrors = [err3];
      } else {
        vErrors.push(err3);
      }
      errors++;
    }
    if (data.openresult !== void 0) {
      let data0 = data.openresult;
      if (typeof data0 === "string") {
        if (!pattern4.test(data0)) {
          const err4 = { instancePath: instancePath + "/openresult", schemaPath: "#/properties/openresult/pattern", keyword: "pattern", params: { pattern: "^\\d+\\.\\d+$" }, message: 'must match pattern "^\\d+\\.\\d+$"', schema: "^\\d+\\.\\d+$", parentSchema: schema31.properties.openresult, data: data0 };
          if (vErrors === null) {
            vErrors = [err4];
          } else {
            vErrors.push(err4);
          }
          errors++;
        }
      } else {
        const err5 = { instancePath: instancePath + "/openresult", schemaPath: "#/properties/openresult/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema31.properties.openresult.type, parentSchema: schema31.properties.openresult, data: data0 };
        if (vErrors === null) {
          vErrors = [err5];
        } else {
          vErrors.push(err5);
        }
        errors++;
      }
    }
    if (data.id !== void 0) {
      let data1 = data.id;
      if (typeof data1 === "string") {
        if (func1(data1) < 1) {
          const err6 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err6];
          } else {
            vErrors.push(err6);
          }
          errors++;
        }
        if (!pattern5.test(data1)) {
          const err7 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z0-9_-]+$" }, message: 'must match pattern "^[A-Za-z0-9_-]+$"', schema: "^[A-Za-z0-9_-]+$", parentSchema: schema32, data: data1 };
          if (vErrors === null) {
            vErrors = [err7];
          } else {
            vErrors.push(err7);
          }
          errors++;
        }
      } else {
        const err8 = { instancePath: instancePath + "/id", schemaPath: "#/$defs/identifier/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema32.type, parentSchema: schema32, data: data1 };
        if (vErrors === null) {
          vErrors = [err8];
        } else {
          vErrors.push(err8);
        }
        errors++;
      }
    }
    if (data.version !== void 0) {
      let data2 = data.version;
      if (!(typeof data2 == "number" && (!(data2 % 1) && !isNaN(data2)))) {
        const err9 = { instancePath: instancePath + "/version", schemaPath: "#/properties/version/type", keyword: "type", params: { type: "integer" }, message: "must be integer", schema: schema31.properties.version.type, parentSchema: schema31.properties.version, data: data2 };
        if (vErrors === null) {
          vErrors = [err9];
        } else {
          vErrors.push(err9);
        }
        errors++;
      }
      if (typeof data2 == "number") {
        if (data2 < 0 || isNaN(data2)) {
          const err10 = { instancePath: instancePath + "/version", schemaPath: "#/properties/version/minimum", keyword: "minimum", params: { comparison: ">=", limit: 0 }, message: "must be >= 0", schema: 0, parentSchema: schema31.properties.version, data: data2 };
          if (vErrors === null) {
            vErrors = [err10];
          } else {
            vErrors.push(err10);
          }
          errors++;
        }
      }
    }
    if (data.status !== void 0) {
      let data3 = data.status;
      if (typeof data3 !== "string") {
        const err11 = { instancePath: instancePath + "/status", schemaPath: "#/properties/status/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema31.properties.status.type, parentSchema: schema31.properties.status, data: data3 };
        if (vErrors === null) {
          vErrors = [err11];
        } else {
          vErrors.push(err11);
        }
        errors++;
      }
      if (!(data3 === "draft" || data3 === "provisional" || data3 === "official" || data3 === "amended")) {
        const err12 = { instancePath: instancePath + "/status", schemaPath: "#/properties/status/enum", keyword: "enum", params: { allowedValues: schema31.properties.status.enum }, message: "must be equal to one of the allowed values", schema: schema31.properties.status.enum, parentSchema: schema31.properties.status, data: data3 };
        if (vErrors === null) {
          vErrors = [err12];
        } else {
          vErrors.push(err12);
        }
        errors++;
      }
    }
    if (data.title !== void 0) {
      let data4 = data.title;
      if (typeof data4 === "string") {
        if (func1(data4) < 1) {
          const err13 = { instancePath: instancePath + "/title", schemaPath: "#/properties/title/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema31.properties.title, data: data4 };
          if (vErrors === null) {
            vErrors = [err13];
          } else {
            vErrors.push(err13);
          }
          errors++;
        }
      } else {
        const err14 = { instancePath: instancePath + "/title", schemaPath: "#/properties/title/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema31.properties.title.type, parentSchema: schema31.properties.title, data: data4 };
        if (vErrors === null) {
          vErrors = [err14];
        } else {
          vErrors.push(err14);
        }
        errors++;
      }
    }
    if (data.description !== void 0) {
      let data5 = data.description;
      if (typeof data5 !== "string") {
        const err15 = { instancePath: instancePath + "/description", schemaPath: "#/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema31.properties.description.type, parentSchema: schema31.properties.description, data: data5 };
        if (vErrors === null) {
          vErrors = [err15];
        } else {
          vErrors.push(err15);
        }
        errors++;
      }
    }
    if (data.lang !== void 0) {
      let data6 = data.lang;
      if (typeof data6 === "string") {
        if (!pattern6.test(data6)) {
          const err16 = { instancePath: instancePath + "/lang", schemaPath: "#/properties/lang/pattern", keyword: "pattern", params: { pattern: "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$" }, message: 'must match pattern "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$"', schema: "^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$", parentSchema: schema31.properties.lang, data: data6 };
          if (vErrors === null) {
            vErrors = [err16];
          } else {
            vErrors.push(err16);
          }
          errors++;
        }
      } else {
        const err17 = { instancePath: instancePath + "/lang", schemaPath: "#/properties/lang/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema31.properties.lang.type, parentSchema: schema31.properties.lang, data: data6 };
        if (vErrors === null) {
          vErrors = [err17];
        } else {
          vErrors.push(err17);
        }
        errors++;
      }
    }
    if (data.generatedAt !== void 0) {
      let data7 = data.generatedAt;
      const _errs19 = errors;
      let valid3 = false;
      const _errs20 = errors;
      if (typeof data7 === "string") {
        if (!formats0.validate(data7)) {
          const err18 = { instancePath: instancePath + "/generatedAt", schemaPath: "#/$defs/timestamp/anyOf/0/format", keyword: "format", params: { format: "date-time" }, message: 'must match format "date-time"', schema: "date-time", parentSchema: schema33.anyOf[0], data: data7 };
          if (vErrors === null) {
            vErrors = [err18];
          } else {
            vErrors.push(err18);
          }
          errors++;
        }
      }
      var _valid0 = _errs20 === errors;
      valid3 = valid3 || _valid0;
      const _errs21 = errors;
      if (typeof data7 === "string") {
        if (!formats2.validate(data7)) {
          const err19 = { instancePath: instancePath + "/generatedAt", schemaPath: "#/$defs/timestamp/anyOf/1/format", keyword: "format", params: { format: "date" }, message: 'must match format "date"', schema: "date", parentSchema: schema33.anyOf[1], data: data7 };
          if (vErrors === null) {
            vErrors = [err19];
          } else {
            vErrors.push(err19);
          }
          errors++;
        }
      }
      var _valid0 = _errs21 === errors;
      valid3 = valid3 || _valid0;
      if (!valid3) {
        const err20 = { instancePath: instancePath + "/generatedAt", schemaPath: "#/$defs/timestamp/anyOf", keyword: "anyOf", params: {}, message: "must match a schema in anyOf", schema: schema33.anyOf, parentSchema: schema33, data: data7 };
        if (vErrors === null) {
          vErrors = [err20];
        } else {
          vErrors.push(err20);
        }
        errors++;
      } else {
        errors = _errs19;
        if (vErrors !== null) {
          if (_errs19) {
            vErrors.length = _errs19;
          } else {
            vErrors = null;
          }
        }
      }
      if (typeof data7 === "string") {
        if (!pattern7.test(data7)) {
          const err21 = { instancePath: instancePath + "/generatedAt", schemaPath: "#/$defs/timestamp/pattern", keyword: "pattern", params: { pattern: "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$" }, message: 'must match pattern "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$"', schema: "^\\d{4}-\\d{2}-\\d{2}(T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2}))?$", parentSchema: schema33, data: data7 };
          if (vErrors === null) {
            vErrors = [err21];
          } else {
            vErrors.push(err21);
          }
          errors++;
        }
      } else {
        const err22 = { instancePath: instancePath + "/generatedAt", schemaPath: "#/$defs/timestamp/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema33.type, parentSchema: schema33, data: data7 };
        if (vErrors === null) {
          vErrors = [err22];
        } else {
          vErrors.push(err22);
        }
        errors++;
      }
    }
    if (data.occurredAt !== void 0) {
      if (!validate21(data.occurredAt, { instancePath: instancePath + "/occurredAt", parentData: data, parentDataProperty: "occurredAt", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate21.errors : vErrors.concat(validate21.errors);
        errors = vErrors.length;
      }
    }
    if (data.source !== void 0) {
      let data9 = data.source;
      if (data9 && typeof data9 == "object" && !Array.isArray(data9)) {
        if (data9.name === void 0) {
          const err23 = { instancePath: instancePath + "/source", schemaPath: "#/$defs/source/required", keyword: "required", params: { missingProperty: "name" }, message: "must have required property 'name'", schema: schema37.required, parentSchema: schema37, data: data9 };
          if (vErrors === null) {
            vErrors = [err23];
          } else {
            vErrors.push(err23);
          }
          errors++;
        }
        if (data9.name !== void 0) {
          let data10 = data9.name;
          if (typeof data10 === "string") {
            if (func1(data10) < 1) {
              const err24 = { instancePath: instancePath + "/source/name", schemaPath: "#/$defs/source/properties/name/minLength", keyword: "minLength", params: { limit: 1 }, message: "must NOT have fewer than 1 characters", schema: 1, parentSchema: schema37.properties.name, data: data10 };
              if (vErrors === null) {
                vErrors = [err24];
              } else {
                vErrors.push(err24);
              }
              errors++;
            }
          } else {
            const err25 = { instancePath: instancePath + "/source/name", schemaPath: "#/$defs/source/properties/name/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema37.properties.name.type, parentSchema: schema37.properties.name, data: data10 };
            if (vErrors === null) {
              vErrors = [err25];
            } else {
              vErrors.push(err25);
            }
            errors++;
          }
        }
        if (data9.description !== void 0) {
          let data11 = data9.description;
          if (typeof data11 !== "string") {
            const err26 = { instancePath: instancePath + "/source/description", schemaPath: "#/$defs/source/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema37.properties.description.type, parentSchema: schema37.properties.description, data: data11 };
            if (vErrors === null) {
              vErrors = [err26];
            } else {
              vErrors.push(err26);
            }
            errors++;
          }
        }
        if (data9.system !== void 0) {
          let data12 = data9.system;
          if (typeof data12 !== "string") {
            const err27 = { instancePath: instancePath + "/source/system", schemaPath: "#/$defs/source/properties/system/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema37.properties.system.type, parentSchema: schema37.properties.system, data: data12 };
            if (vErrors === null) {
              vErrors = [err27];
            } else {
              vErrors.push(err27);
            }
            errors++;
          }
        }
        if (data9.url !== void 0) {
          let data13 = data9.url;
          if (typeof data13 === "string") {
            if (!formats12(data13)) {
              const err28 = { instancePath: instancePath + "/source/url", schemaPath: "#/$defs/source/properties/url/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema37.properties.url, data: data13 };
              if (vErrors === null) {
                vErrors = [err28];
              } else {
                vErrors.push(err28);
              }
              errors++;
            }
          } else {
            const err29 = { instancePath: instancePath + "/source/url", schemaPath: "#/$defs/source/properties/url/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema37.properties.url.type, parentSchema: schema37.properties.url, data: data13 };
            if (vErrors === null) {
              vErrors = [err29];
            } else {
              vErrors.push(err29);
            }
            errors++;
          }
        }
        if (data9.license !== void 0) {
          let data14 = data9.license;
          if (typeof data14 !== "string") {
            const err30 = { instancePath: instancePath + "/source/license", schemaPath: "#/$defs/source/properties/license/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema37.properties.license.type, parentSchema: schema37.properties.license, data: data14 };
            if (vErrors === null) {
              vErrors = [err30];
            } else {
              vErrors.push(err30);
            }
            errors++;
          }
        }
        if (data9.contact !== void 0) {
          let data15 = data9.contact;
          if (typeof data15 !== "string") {
            const err31 = { instancePath: instancePath + "/source/contact", schemaPath: "#/$defs/source/properties/contact/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema37.properties.contact.type, parentSchema: schema37.properties.contact, data: data15 };
            if (vErrors === null) {
              vErrors = [err31];
            } else {
              vErrors.push(err31);
            }
            errors++;
          }
        }
        var props0 = {};
        props0.name = true;
        props0.description = true;
        props0.system = true;
        props0.url = true;
        props0.license = true;
        props0.contact = true;
        for (const key0 in data9) {
          if (pattern10.test(key0)) {
            props0[key0] = true;
          }
        }
        if (props0 !== true) {
          for (const key1 in data9) {
            if (!props0 || !props0[key1]) {
              const err32 = { instancePath: instancePath + "/source", schemaPath: "#/$defs/source/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key1 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema37, data: data9 };
              if (vErrors === null) {
                vErrors = [err32];
              } else {
                vErrors.push(err32);
              }
              errors++;
            }
          }
        }
      } else {
        const err33 = { instancePath: instancePath + "/source", schemaPath: "#/$defs/source/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema37.type, parentSchema: schema37, data: data9 };
        if (vErrors === null) {
          vErrors = [err33];
        } else {
          vErrors.push(err33);
        }
        errors++;
      }
    }
    if (data.measures !== void 0) {
      let data16 = data.measures;
      if (Array.isArray(data16)) {
        const len0 = data16.length;
        for (let i0 = 0; i0 < len0; i0++) {
          if (!validate23(data16[i0], { instancePath: instancePath + "/measures/" + i0, parentData: data16, parentDataProperty: i0, rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate23.errors : vErrors.concat(validate23.errors);
            errors = vErrors.length;
          }
        }
      } else {
        const err34 = { instancePath: instancePath + "/measures", schemaPath: "#/properties/measures/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.measures.type, parentSchema: schema31.properties.measures, data: data16 };
        if (vErrors === null) {
          vErrors = [err34];
        } else {
          vErrors.push(err34);
        }
        errors++;
      }
    }
    if (data.attributeDefinitions !== void 0) {
      let data18 = data.attributeDefinitions;
      if (Array.isArray(data18)) {
        const len1 = data18.length;
        for (let i1 = 0; i1 < len1; i1++) {
          if (!validate25(data18[i1], { instancePath: instancePath + "/attributeDefinitions/" + i1, parentData: data18, parentDataProperty: i1, rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate25.errors : vErrors.concat(validate25.errors);
            errors = vErrors.length;
          }
        }
      } else {
        const err35 = { instancePath: instancePath + "/attributeDefinitions", schemaPath: "#/properties/attributeDefinitions/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.attributeDefinitions.type, parentSchema: schema31.properties.attributeDefinitions, data: data18 };
        if (vErrors === null) {
          vErrors = [err35];
        } else {
          vErrors.push(err35);
        }
        errors++;
      }
    }
    if (data.attributes !== void 0) {
      let data20 = data.attributes;
      if (data20 && typeof data20 == "object" && !Array.isArray(data20)) {
        for (const key2 in data20) {
          let data21 = data20[key2];
          if (typeof data21 !== "string" && !(typeof data21 == "number") && typeof data21 !== "boolean") {
            const err36 = { instancePath: instancePath + "/attributes/" + key2.replace(/~/g, "~0").replace(/\//g, "~1"), schemaPath: "#/$defs/attributeValues/additionalProperties/type", keyword: "type", params: { type: schema42.additionalProperties.type }, message: "must be string,number,boolean", schema: schema42.additionalProperties.type, parentSchema: schema42.additionalProperties, data: data21 };
            if (vErrors === null) {
              vErrors = [err36];
            } else {
              vErrors.push(err36);
            }
            errors++;
          }
        }
      } else {
        const err37 = { instancePath: instancePath + "/attributes", schemaPath: "#/$defs/attributeValues/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema42.type, parentSchema: schema42, data: data20 };
        if (vErrors === null) {
          vErrors = [err37];
        } else {
          vErrors.push(err37);
        }
        errors++;
      }
    }
    if (data.participants !== void 0) {
      let data22 = data.participants;
      if (Array.isArray(data22)) {
        const len2 = data22.length;
        for (let i2 = 0; i2 < len2; i2++) {
          if (!validate27(data22[i2], { instancePath: instancePath + "/participants/" + i2, parentData: data22, parentDataProperty: i2, rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate27.errors : vErrors.concat(validate27.errors);
            errors = vErrors.length;
          }
        }
      } else {
        const err38 = { instancePath: instancePath + "/participants", schemaPath: "#/properties/participants/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.participants.type, parentSchema: schema31.properties.participants, data: data22 };
        if (vErrors === null) {
          vErrors = [err38];
        } else {
          vErrors.push(err38);
        }
        errors++;
      }
    }
    if (data.events !== void 0) {
      let data24 = data.events;
      if (Array.isArray(data24)) {
        const len3 = data24.length;
        for (let i3 = 0; i3 < len3; i3++) {
          if (!validate31(data24[i3], { instancePath: instancePath + "/events/" + i3, parentData: data24, parentDataProperty: i3, rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate31.errors : vErrors.concat(validate31.errors);
            errors = vErrors.length;
          }
        }
      } else {
        const err39 = { instancePath: instancePath + "/events", schemaPath: "#/properties/events/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.events.type, parentSchema: schema31.properties.events, data: data24 };
        if (vErrors === null) {
          vErrors = [err39];
        } else {
          vErrors.push(err39);
        }
        errors++;
      }
    }
    if (data.results !== void 0) {
      let data26 = data.results;
      if (Array.isArray(data26)) {
        const len4 = data26.length;
        for (let i4 = 0; i4 < len4; i4++) {
          if (!validate35(data26[i4], { instancePath: instancePath + "/results/" + i4, parentData: data26, parentDataProperty: i4, rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate35.errors : vErrors.concat(validate35.errors);
            errors = vErrors.length;
          }
        }
      } else {
        const err40 = { instancePath: instancePath + "/results", schemaPath: "#/properties/results/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.results.type, parentSchema: schema31.properties.results, data: data26 };
        if (vErrors === null) {
          vErrors = [err40];
        } else {
          vErrors.push(err40);
        }
        errors++;
      }
    }
    if (data.rankings !== void 0) {
      let data28 = data.rankings;
      if (Array.isArray(data28)) {
        const len5 = data28.length;
        for (let i5 = 0; i5 < len5; i5++) {
          if (!validate37(data28[i5], { instancePath: instancePath + "/rankings/" + i5, parentData: data28, parentDataProperty: i5, rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate37.errors : vErrors.concat(validate37.errors);
            errors = vErrors.length;
          }
        }
      } else {
        const err41 = { instancePath: instancePath + "/rankings", schemaPath: "#/properties/rankings/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.rankings.type, parentSchema: schema31.properties.rankings, data: data28 };
        if (vErrors === null) {
          vErrors = [err41];
        } else {
          vErrors.push(err41);
        }
        errors++;
      }
    }
    if (data.categories !== void 0) {
      let data30 = data.categories;
      if (Array.isArray(data30)) {
        const len6 = data30.length;
        for (let i6 = 0; i6 < len6; i6++) {
          if (!validate41(data30[i6], { instancePath: instancePath + "/categories/" + i6, parentData: data30, parentDataProperty: i6, rootData, dynamicAnchors })) {
            vErrors = vErrors === null ? validate41.errors : vErrors.concat(validate41.errors);
            errors = vErrors.length;
          }
        }
      } else {
        const err42 = { instancePath: instancePath + "/categories", schemaPath: "#/properties/categories/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.categories.type, parentSchema: schema31.properties.categories, data: data30 };
        if (vErrors === null) {
          vErrors = [err42];
        } else {
          vErrors.push(err42);
        }
        errors++;
      }
    }
    if (data.links !== void 0) {
      let data32 = data.links;
      if (Array.isArray(data32)) {
        const len7 = data32.length;
        for (let i7 = 0; i7 < len7; i7++) {
          let data33 = data32[i7];
          if (data33 && typeof data33 == "object" && !Array.isArray(data33)) {
            if (data33.href === void 0) {
              const err43 = { instancePath: instancePath + "/links/" + i7, schemaPath: "#/$defs/link/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema48.required, parentSchema: schema48, data: data33 };
              if (vErrors === null) {
                vErrors = [err43];
              } else {
                vErrors.push(err43);
              }
              errors++;
            }
            if (data33.rel !== void 0) {
              let data34 = data33.rel;
              if (typeof data34 !== "string") {
                const err44 = { instancePath: instancePath + "/links/" + i7 + "/rel", schemaPath: "#/$defs/link/properties/rel/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.rel.type, parentSchema: schema48.properties.rel, data: data34 };
                if (vErrors === null) {
                  vErrors = [err44];
                } else {
                  vErrors.push(err44);
                }
                errors++;
              }
            }
            if (data33.href !== void 0) {
              let data35 = data33.href;
              if (typeof data35 === "string") {
                if (!formats12(data35)) {
                  const err45 = { instancePath: instancePath + "/links/" + i7 + "/href", schemaPath: "#/$defs/link/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema48.properties.href, data: data35 };
                  if (vErrors === null) {
                    vErrors = [err45];
                  } else {
                    vErrors.push(err45);
                  }
                  errors++;
                }
              } else {
                const err46 = { instancePath: instancePath + "/links/" + i7 + "/href", schemaPath: "#/$defs/link/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.href.type, parentSchema: schema48.properties.href, data: data35 };
                if (vErrors === null) {
                  vErrors = [err46];
                } else {
                  vErrors.push(err46);
                }
                errors++;
              }
            }
            if (data33.label !== void 0) {
              let data36 = data33.label;
              if (typeof data36 !== "string") {
                const err47 = { instancePath: instancePath + "/links/" + i7 + "/label", schemaPath: "#/$defs/link/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.label.type, parentSchema: schema48.properties.label, data: data36 };
                if (vErrors === null) {
                  vErrors = [err47];
                } else {
                  vErrors.push(err47);
                }
                errors++;
              }
            }
            if (data33.description !== void 0) {
              let data37 = data33.description;
              if (typeof data37 !== "string") {
                const err48 = { instancePath: instancePath + "/links/" + i7 + "/description", schemaPath: "#/$defs/link/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema48.properties.description.type, parentSchema: schema48.properties.description, data: data37 };
                if (vErrors === null) {
                  vErrors = [err48];
                } else {
                  vErrors.push(err48);
                }
                errors++;
              }
            }
            var props1 = {};
            props1.rel = true;
            props1.href = true;
            props1.label = true;
            props1.description = true;
            for (const key3 in data33) {
              if (pattern10.test(key3)) {
                props1[key3] = true;
              }
            }
            if (props1 !== true) {
              for (const key4 in data33) {
                if (!props1 || !props1[key4]) {
                  const err49 = { instancePath: instancePath + "/links/" + i7, schemaPath: "#/$defs/link/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key4 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema48, data: data33 };
                  if (vErrors === null) {
                    vErrors = [err49];
                  } else {
                    vErrors.push(err49);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err50 = { instancePath: instancePath + "/links/" + i7, schemaPath: "#/$defs/link/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema48.type, parentSchema: schema48, data: data33 };
            if (vErrors === null) {
              vErrors = [err50];
            } else {
              vErrors.push(err50);
            }
            errors++;
          }
        }
      } else {
        const err51 = { instancePath: instancePath + "/links", schemaPath: "#/properties/links/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.links.type, parentSchema: schema31.properties.links, data: data32 };
        if (vErrors === null) {
          vErrors = [err51];
        } else {
          vErrors.push(err51);
        }
        errors++;
      }
    }
    if (data.assets !== void 0) {
      let data38 = data.assets;
      if (Array.isArray(data38)) {
        const len8 = data38.length;
        for (let i8 = 0; i8 < len8; i8++) {
          let data39 = data38[i8];
          if (data39 && typeof data39 == "object" && !Array.isArray(data39)) {
            if (data39.href === void 0) {
              const err52 = { instancePath: instancePath + "/assets/" + i8, schemaPath: "#/$defs/asset/required", keyword: "required", params: { missingProperty: "href" }, message: "must have required property 'href'", schema: schema49.required, parentSchema: schema49, data: data39 };
              if (vErrors === null) {
                vErrors = [err52];
              } else {
                vErrors.push(err52);
              }
              errors++;
            }
            if (data39.type !== void 0) {
              let data40 = data39.type;
              if (typeof data40 !== "string") {
                const err53 = { instancePath: instancePath + "/assets/" + i8 + "/type", schemaPath: "#/$defs/asset/properties/type/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.type.type, parentSchema: schema49.properties.type, data: data40 };
                if (vErrors === null) {
                  vErrors = [err53];
                } else {
                  vErrors.push(err53);
                }
                errors++;
              }
              if (!(data40 === "image" || data40 === "video" || data40 === "audio" || data40 === "document" || data40 === "other")) {
                const err54 = { instancePath: instancePath + "/assets/" + i8 + "/type", schemaPath: "#/$defs/asset/properties/type/enum", keyword: "enum", params: { allowedValues: schema49.properties.type.enum }, message: "must be equal to one of the allowed values", schema: schema49.properties.type.enum, parentSchema: schema49.properties.type, data: data40 };
                if (vErrors === null) {
                  vErrors = [err54];
                } else {
                  vErrors.push(err54);
                }
                errors++;
              }
            }
            if (data39.href !== void 0) {
              let data41 = data39.href;
              if (typeof data41 === "string") {
                if (!formats12(data41)) {
                  const err55 = { instancePath: instancePath + "/assets/" + i8 + "/href", schemaPath: "#/$defs/asset/properties/href/format", keyword: "format", params: { format: "uri" }, message: 'must match format "uri"', schema: "uri", parentSchema: schema49.properties.href, data: data41 };
                  if (vErrors === null) {
                    vErrors = [err55];
                  } else {
                    vErrors.push(err55);
                  }
                  errors++;
                }
              } else {
                const err56 = { instancePath: instancePath + "/assets/" + i8 + "/href", schemaPath: "#/$defs/asset/properties/href/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.href.type, parentSchema: schema49.properties.href, data: data41 };
                if (vErrors === null) {
                  vErrors = [err56];
                } else {
                  vErrors.push(err56);
                }
                errors++;
              }
            }
            if (data39.label !== void 0) {
              let data42 = data39.label;
              if (typeof data42 !== "string") {
                const err57 = { instancePath: instancePath + "/assets/" + i8 + "/label", schemaPath: "#/$defs/asset/properties/label/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.label.type, parentSchema: schema49.properties.label, data: data42 };
                if (vErrors === null) {
                  vErrors = [err57];
                } else {
                  vErrors.push(err57);
                }
                errors++;
              }
            }
            if (data39.description !== void 0) {
              let data43 = data39.description;
              if (typeof data43 !== "string") {
                const err58 = { instancePath: instancePath + "/assets/" + i8 + "/description", schemaPath: "#/$defs/asset/properties/description/type", keyword: "type", params: { type: "string" }, message: "must be string", schema: schema49.properties.description.type, parentSchema: schema49.properties.description, data: data43 };
                if (vErrors === null) {
                  vErrors = [err58];
                } else {
                  vErrors.push(err58);
                }
                errors++;
              }
            }
            var props2 = {};
            props2.type = true;
            props2.href = true;
            props2.label = true;
            props2.description = true;
            for (const key5 in data39) {
              if (pattern10.test(key5)) {
                props2[key5] = true;
              }
            }
            if (props2 !== true) {
              for (const key6 in data39) {
                if (!props2 || !props2[key6]) {
                  const err59 = { instancePath: instancePath + "/assets/" + i8, schemaPath: "#/$defs/asset/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key6 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema49, data: data39 };
                  if (vErrors === null) {
                    vErrors = [err59];
                  } else {
                    vErrors.push(err59);
                  }
                  errors++;
                }
              }
            }
          } else {
            const err60 = { instancePath: instancePath + "/assets/" + i8, schemaPath: "#/$defs/asset/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema49.type, parentSchema: schema49, data: data39 };
            if (vErrors === null) {
              vErrors = [err60];
            } else {
              vErrors.push(err60);
            }
            errors++;
          }
        }
      } else {
        const err61 = { instancePath: instancePath + "/assets", schemaPath: "#/properties/assets/type", keyword: "type", params: { type: "array" }, message: "must be array", schema: schema31.properties.assets.type, parentSchema: schema31.properties.assets, data: data38 };
        if (vErrors === null) {
          vErrors = [err61];
        } else {
          vErrors.push(err61);
        }
        errors++;
      }
    }
    if (data.presentation !== void 0) {
      if (!validate44(data.presentation, { instancePath: instancePath + "/presentation", parentData: data, parentDataProperty: "presentation", rootData, dynamicAnchors })) {
        vErrors = vErrors === null ? validate44.errors : vErrors.concat(validate44.errors);
        errors = vErrors.length;
      }
    }
    var props3 = {};
    props3.openresult = true;
    props3.id = true;
    props3.version = true;
    props3.status = true;
    props3.title = true;
    props3.description = true;
    props3.lang = true;
    props3.generatedAt = true;
    props3.occurredAt = true;
    props3.source = true;
    props3.measures = true;
    props3.attributeDefinitions = true;
    props3.attributes = true;
    props3.participants = true;
    props3.events = true;
    props3.results = true;
    props3.rankings = true;
    props3.categories = true;
    props3.links = true;
    props3.assets = true;
    props3.presentation = true;
    for (const key7 in data) {
      if (pattern10.test(key7)) {
        props3[key7] = true;
      }
    }
    if (props3 !== true) {
      for (const key8 in data) {
        if (!props3 || !props3[key8]) {
          const err62 = { instancePath, schemaPath: "#/unevaluatedProperties", keyword: "unevaluatedProperties", params: { unevaluatedProperty: key8 }, message: "must NOT have unevaluated properties", schema: false, parentSchema: schema31, data };
          if (vErrors === null) {
            vErrors = [err62];
          } else {
            vErrors.push(err62);
          }
          errors++;
        }
      }
    }
  } else {
    const err63 = { instancePath, schemaPath: "#/type", keyword: "type", params: { type: "object" }, message: "must be object", schema: schema31.type, parentSchema: schema31, data };
    if (vErrors === null) {
      vErrors = [err63];
    } else {
      vErrors.push(err63);
    }
    errors++;
  }
  validate20.errors = vErrors;
  return errors === 0;
}
validate20.evaluated = { "props": true, "dynamicProps": false, "dynamicItems": false };
const validator = validate20;
function checkSchema(document) {
  if (validator(document)) return [];
  const later = declaresLaterMinor(document);
  return (validator.errors ?? []).flatMap((error) => translate(error, later));
}
function declaresLaterMinor(document) {
  if (typeof document !== "object" || document === null) return false;
  const declared = document["openresult"];
  if (typeof declared !== "string") return false;
  const [major, minor] = declared.split(".");
  return Number(major) === SUPPORTED_VERSION.major && Number(minor) > SUPPORTED_VERSION.minor;
}
function translate(error, laterMinor) {
  const path = error.instancePath;
  switch (error.keyword) {
    case "required": {
      const missing = String(error.params["missingProperty"]);
      const isUnit = missing === "unit";
      return [
        make(
          isUnit ? "OR-107" : "OR-101",
          path,
          isUnit ? `This measure has no "unit". Every kind except text and boolean needs one, otherwise a value is a bare number.` : `The "${missing}" member is required here and is missing.`,
          isUnit ? `Add "unit", for example "s" for a duration or "pt" for points.` : `Add "${missing}".`
        )
      ];
    }
    case "type": {
      const expected = formatExpected(error.params["type"]);
      if (error.data === null) {
        return [
          make(
            "OR-108",
            path,
            `This value is null. An unavailable measure is omitted, not set to null — otherwise nothing distinguishes "not recorded" from "recorded as nothing".`,
            `Remove this key from "values".`
          )
        ];
      }
      return [
        make(
          "OR-102",
          path,
          `This value should be ${expected} but is ${describe(error.data)}.`,
          `Provide ${expected}.`
        )
      ];
    }
    // The one conditional in the schema: `unit` on an attribute forces the type
    // to `number`. Ajv reports it as a failed `const`, which reads as "must be
    // equal to constant" and helps nobody.
    case "const": {
      if (path.endsWith("/type")) {
        return [
          make(
            "OR-110",
            path,
            `Only a number attribute may declare a "unit": a unit describes a quantity, and this attribute is ${describe(error.data)}.`,
            `Drop the "unit", or change "type" to "number" if the values really are numbers.`
          )
        ];
      }
      return [
        make(
          "OR-103",
          path,
          `${describe(error.data)} is not the value required here.`,
          `Use the value the schema fixes for this member.`
        )
      ];
    }
    case "enum": {
      const allowed = error.params["allowedValues"].map((v) => `"${String(v)}"`);
      if (laterMinor) {
        return [
          make(
            "OR-914",
            path,
            `${describe(error.data)} is not a value this version defines. The document declares a later minor version, so this is read as the fallback its domain fixes (spec §11.3.1) rather than rejected.`,
            `Nothing to fix if the value belongs to that later version. If it is a typo, use one of: ${allowed.join(", ")}.`
          )
        ];
      }
      return [
        make(
          "OR-103",
          path,
          `${describe(error.data)} is not one of the values this member accepts.`,
          `Use one of: ${allowed.join(", ")}.`
        )
      ];
    }
    case "pattern": {
      const pattern = String(error.params["pattern"]);
      if (pattern.includes("A-Za-z0-9_-")) {
        return [
          make(
            "OR-104",
            path,
            `${describe(error.data)} is not a valid identifier. Identifiers may contain only letters, digits, hyphens and underscores, so that they survive being placed in a URL or a selector.`,
            `Replace any other character, for example with a hyphen.`
          )
        ];
      }
      if (pattern.includes("\\d{4}-\\d{2}-\\d{2}")) {
        return [
          make(
            "OR-106",
            path,
            `${describe(error.data)} is not an RFC 3339 timestamp with a UTC offset.`,
            `Use a form like "2026-05-17T16:42:00+02:00", or "2026-05-17" for a bare date.`
          )
        ];
      }
      return [
        make(
          "OR-102",
          path,
          `${describe(error.data)} does not have the expected form.`,
          `Check the specification for this member's format.`
        )
      ];
    }
    case "unevaluatedProperties":
    case "additionalProperties": {
      const name = String(
        error.params["unevaluatedProperty"] ?? error.params["additionalProperty"]
      );
      return [
        make(
          "OR-105",
          `${path}/${name}`,
          `"${name}" is not a member this specification defines. Unknown members are rejected rather than ignored, so that a typo does not become silently discarded data.`,
          `Correct the spelling, or prefix it with "x-" if it is a deliberate extension: "x-${name}".`
        )
      ];
    }
    // Structural keywords that only exist to route validation. Reporting them
    // would tell a producer about the schema's internals, not about the mistake.
    //
    // `propertyNames` belongs here for the same reason with one extra step: the
    // subschema it applies to the key has already reported the real error — a
    // ranking id with a space in it comes back as OR-104, naming the key and
    // saying what an identifier may contain. Ajv then adds "property name must
    // be valid" on top, which is a second error for one mistake and the one
    // that helps least.
    case "if":
    case "else":
    case "then":
    case "anyOf":
    case "allOf":
    case "oneOf":
    case "propertyNames":
      return [];
    case "minItems":
      return [make("OR-304", path, `This list must not be empty.`, `Add at least one entry.`)];
    case "minLength":
      return [make("OR-102", path, `This text must not be empty.`, `Provide a value.`)];
    case "minimum":
      return [
        make(
          "OR-102",
          path,
          `${describe(error.data)} is below the minimum this member allows (${String(error.params["limit"])}).`,
          `Use a value of at least ${String(error.params["limit"])}.`
        )
      ];
    case "format":
      return [
        make(
          "OR-102",
          path,
          `${describe(error.data)} is not a valid ${String(error.params["format"])}.`,
          `Provide a well-formed ${String(error.params["format"])}.`
        )
      ];
    default:
      return [
        make(
          "OR-102",
          path,
          error.message ?? `This value does not satisfy the schema.`,
          `Check this member against the specification. Reaching this reply means the schema grew a keyword nothing here translates, which is a defect in the validator as much as in the document.`
        )
      ];
  }
}
function make(code, path, message, suggestion) {
  return diagnostic(code, path === "" ? "/" : path, message, suggestion);
}
function formatExpected(type) {
  if (Array.isArray(type)) {
    const names = type.map(String);
    const last = names.pop();
    return `${names.join(", ")} or ${last ?? ""}`;
  }
  return String(type);
}
function describe(value) {
  if (value === null) return "null";
  if (typeof value === "string") return `"${value}"`;
  if (Array.isArray(value)) return "a list";
  if (typeof value === "object") return "an object";
  return String(value);
}
function checkReferences(document) {
  const found = [];
  const participantIds = collectIds(document.participants);
  const measureIds = collectIds(document.measures ?? []);
  const attributeIds = new Map(
    (document.attributeDefinitions ?? []).map((entry) => [entry.id, entry])
  );
  const eventIds = collectIds(document.events ?? []);
  const categoryIds = collectIds(document.categories ?? []);
  found.push(...duplicates(document.participants, "participants"));
  found.push(...duplicates(document.measures ?? [], "measures"));
  found.push(...duplicates(document.attributeDefinitions ?? [], "attributeDefinitions"));
  found.push(...duplicates(document.events ?? [], "events"));
  found.push(...duplicates(document.categories ?? [], "categories"));
  found.push(...duplicates(document.rankings ?? [], "rankings"));
  document.participants.forEach((participant, index) => {
    participant.members?.forEach((member, memberIndex) => {
      if (!participantIds.has(member)) {
        found.push(
          diagnostic(
            "OR-201",
            pointer("participants", index, "members", memberIndex),
            `"${participant.name}" lists "${member}" as a member, but no participant declares that id.`,
            `Declare a participant with id "${member}", or remove it from the roster.`
          )
        );
      }
    });
  });
  (document.events ?? []).forEach((event, index) => {
    if (event.parent !== void 0 && !eventIds.has(event.parent)) {
      found.push(
        diagnostic(
          "OR-201",
          pointer("events", index, "parent"),
          `Event "${event.name}" is attached to parent "${event.parent}", which is not declared.`,
          `Declare an event with id "${event.parent}", or remove the "parent" member.`
        )
      );
    }
  });
  found.push(...cycles(document.events ?? [], "events"));
  found.push(...cycles(document.categories ?? [], "categories"));
  (document.categories ?? []).forEach((category, index) => {
    if (category.parent !== void 0 && !categoryIds.has(category.parent)) {
      found.push(
        diagnostic(
          "OR-201",
          pointer("categories", index, "parent"),
          `Category "${category.label}" is attached to parent "${category.parent}", which is not declared.`,
          `Declare a category with id "${category.parent}", or remove the "parent" member.`
        )
      );
    }
    category.participants?.forEach((member, memberIndex) => {
      if (!participantIds.has(member)) {
        found.push(
          diagnostic(
            "OR-201",
            pointer("categories", index, "participants", memberIndex),
            `Category "${category.label}" includes "${member}", which is not a declared participant.`,
            `Declare a participant with id "${member}", or remove it from the category.`
          )
        );
      }
    });
  });
  const seenPairs = /* @__PURE__ */ new Map();
  const documentHasEvents = (document.events ?? []).length > 0;
  document.results.forEach((result, index) => {
    if (result.participant !== void 0 && !participantIds.has(result.participant)) {
      found.push(
        diagnostic(
          "OR-201",
          pointer("results", index, "participant"),
          `This result belongs to "${result.participant}", which is not a declared participant.`,
          `Add "${result.participant}" to "participants", or correct the reference.`
        )
      );
    }
    if (result.event !== void 0 && !eventIds.has(result.event)) {
      found.push(
        diagnostic(
          "OR-201",
          pointer("results", index, "event"),
          `This result is attached to event "${result.event}", which is not declared.`,
          `Declare an event with id "${result.event}", or correct the reference.`
        )
      );
    } else if (result.event === void 0 && documentHasEvents) {
      found.push(
        diagnostic(
          "OR-201",
          pointer("results", index),
          `The document declares events, so every result must say which one it belongs to.`,
          `Add an "event" member naming the event this result comes from.`
        )
      );
    }
    const pair = `${result.participant}\0${result.event ?? ""}`;
    const previous = seenPairs.get(pair);
    if (previous !== void 0) {
      found.push(
        diagnostic(
          "OR-203",
          pointer("results", index),
          `"${result.participant}" already has a result for this event at /results/${previous}.`,
          `A participant holds at most one result per event. Merge the two, or attach one of them to a different event.`
        )
      );
    } else {
      seenPairs.set(pair, index);
    }
    found.push(...unknownAttributes(document.attributes, attributeIds, ""));
    for (const [key, value] of Object.entries(result.values ?? {})) {
      const measure2 = document.measures?.find((candidate) => candidate.id === key);
      if (measure2 === void 0) {
        found.push(
          diagnostic(
            "OR-205",
            pointer("results", index, "values", key),
            `"${key}" is not a declared measure, so nothing says what this value means.`,
            `Declare "${key}" in "measures" with its kind, unit and betterWhen.`
          )
        );
        continue;
      }
      const wanted = expectedType(measure2.kind);
      if (wanted !== void 0 && typeof value !== wanted) {
        found.push(
          diagnostic(
            "OR-102",
            pointer("results", index, "values", key),
            `"${measure2.label}" is a ${measure2.kind} measure, so this value must be a ${wanted}; it is a ${typeof value}.`,
            wanted === "number" ? `Write the value as a number in ${measure2.unit ?? "the declared unit"}, without quotes.` : `Provide a ${wanted}.`
          )
        );
      }
    }
    found.push(...unknownAttributes(result.attributes, attributeIds, pointer("results", index)));
  });
  document.participants.forEach((participant, index) => {
    found.push(
      ...unknownAttributes(participant.attributes, attributeIds, pointer("participants", index))
    );
  });
  (document.events ?? []).forEach((event, index) => {
    found.push(...unknownAttributes(event.attributes, attributeIds, pointer("events", index)));
  });
  (document.categories ?? []).forEach((category, index) => {
    found.push(
      ...unknownAttributes(category.attributes, attributeIds, pointer("categories", index))
    );
  });
  (document.rankings ?? []).forEach((ranking, index) => {
    const scoped = ranking.scope?.event;
    const scopedEvents = scoped === void 0 ? [] : Array.isArray(scoped) ? scoped : [scoped];
    scopedEvents.forEach((eventId, eventIndex) => {
      if (eventIds.has(eventId)) return;
      found.push(
        diagnostic(
          "OR-201",
          Array.isArray(scoped) ? pointer("rankings", index, "scope", "event", eventIndex) : pointer("rankings", index, "scope", "event"),
          `Ranking "${ranking.label}" is scoped to event "${eventId}", which is not declared.`,
          `Declare that event, or drop the scope to rank over the whole document.`
        )
      );
    });
    const scopedCategory = ranking.scope?.category;
    const scopedCategories = scopedCategory === void 0 ? [] : Array.isArray(scopedCategory) ? scopedCategory : [scopedCategory];
    scopedCategories.forEach((categoryId, categoryIndex) => {
      if (categoryIds.has(categoryId)) return;
      found.push(
        diagnostic(
          "OR-201",
          Array.isArray(scopedCategory) ? pointer("rankings", index, "scope", "category", categoryIndex) : pointer("rankings", index, "scope", "category"),
          `Ranking "${ranking.label}" is scoped to category "${categoryId}", which is not declared.`,
          `Declare that category, or drop the scope.`
        )
      );
    });
    ranking.sortBy.forEach((measureId, measureIndex) => {
      if (!measureIds.has(measureId)) {
        found.push(
          diagnostic(
            "OR-201",
            pointer("rankings", index, "sortBy", measureIndex),
            `Ranking "${ranking.label}" sorts on "${measureId}", which is not a declared measure.`,
            `Declare "${measureId}" in "measures", or remove it from sortBy.`
          )
        );
      }
    });
  });
  return found;
}
function collectIds(entries) {
  return new Set(entries.map((entry) => entry.id));
}
function duplicates(entries, collection) {
  const seen = /* @__PURE__ */ new Map();
  const found = [];
  entries.forEach((entry, index) => {
    const previous = seen.get(entry.id);
    if (previous !== void 0) {
      found.push(
        diagnostic(
          "OR-202",
          pointer(collection, index, "id"),
          `The id "${entry.id}" is already used at /${collection}/${previous}. References would be ambiguous.`,
          `Give this entry a distinct id.`
        )
      );
    } else {
      seen.set(entry.id, index);
    }
  });
  return found;
}
function unknownAttributes(attributes, declared, basePath) {
  const found = [];
  for (const [key, value] of Object.entries(attributes ?? {})) {
    const definition = declared.get(key);
    if (definition === void 0) {
      found.push(
        diagnostic(
          "OR-206",
          `${basePath}/attributes/${key}`,
          `"${key}" is not a declared attribute, so a consumer cannot label or type it.`,
          `Declare "${key}" in "attributeDefinitions" with its label and type.`
        )
      );
      continue;
    }
    const complaint = attributeComplaint(definition, value);
    if (complaint !== null) {
      found.push(
        diagnostic("OR-102", `${basePath}/attributes/${key}`, complaint.message, complaint.fix)
      );
    }
  }
  return found;
}
function attributeComplaint(definition, value) {
  const label = `"${definition.label}" is declared as ${definition.type}`;
  switch (definition.type) {
    case "number":
      return typeof value === "number" ? null : {
        message: `${label}, so this value must be a number, not a ${typeof value}.`,
        fix: "Write the value without quotes."
      };
    case "boolean":
      return typeof value === "boolean" ? null : {
        message: `${label}, so this value must be true or false.`,
        fix: "Write the value without quotes."
      };
    case "url":
      return typeof value === "string" && /^[a-z][a-z0-9+.-]*:/i.test(value) ? null : {
        message: `${label}, so this value must be an absolute URI.`,
        fix: "Include the scheme, for example https://example.org/…"
      };
    case "date":
      return typeof value === "string" && /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2}))?$/.test(value) ? null : {
        message: `${label}, so this value must be an RFC 3339 date or date-time.`,
        fix: 'Use a form like "2026-05-17" or "2026-05-17T16:42:00+02:00".'
      };
    case "country":
      return typeof value === "string" && /^[A-Z]{2}$/.test(value) ? null : {
        message: `${label}, so this value must be an ISO 3166-1 alpha-2 code in upper case.`,
        fix: 'Use the two-letter code, for example "FR". For alpha-3 or IOC codes, declare a separate text attribute.'
      };
    case "text":
      return typeof value === "string" ? null : { message: `${label}, so this value must be a string.`, fix: "Quote the value." };
    default:
      return null;
  }
}
function cycles(entries, collection) {
  const parents = new Map(entries.map((entry) => [entry.id, entry.parent]));
  const found = [];
  entries.forEach((entry, index) => {
    const visited = /* @__PURE__ */ new Set([entry.id]);
    let current = entry.parent;
    while (current !== void 0) {
      if (visited.has(current)) {
        found.push(
          diagnostic(
            "OR-204",
            pointer(collection, index, "parent"),
            `"${entry.id}" is its own ancestor: following "parent" leads back to it.`,
            `Break the loop — a hierarchy must have a root.`
          )
        );
        return;
      }
      visited.add(current);
      current = parents.get(current);
    }
  });
  return found;
}
function expectedType(kind) {
  if (!KNOWN_KINDS.has(kind)) return void 0;
  if (kind === "text") return "string";
  if (kind === "boolean") return "boolean";
  return "number";
}
function checkRanking(document) {
  const found = [];
  (document.rankings ?? []).forEach((ranking, index) => {
    const orderedByPublishedRanks = ranking.sortBy.length === 0 && normalizeTies(ranking.ties) === "resolved";
    if (ranking.sortBy.length === 0 && !orderedByPublishedRanks) {
      found.push(
        diagnostic(
          "OR-304",
          pointer("rankings", index, "sortBy"),
          `Ranking "${ranking.label}" lists no measure to sort on, so it cannot order anything.`,
          `Add at least one measure id to sortBy, or declare ties: "resolved" and publish a position for each result.`
        )
      );
    }
    if (orderedByPublishedRanks) {
      const excluded = ranking.excludeStatuses ?? DEFAULT_EXCLUDED_STATUSES;
      const selected = rank(document, ranking.id).filter(
        (entry) => !excluded.includes(normalizeStatus(entry.result.status))
      );
      const without = selected.filter((entry) => entry.result.ranks?.[ranking.id] === void 0);
      if (without.length > 0) {
        const names = without.slice(0, 3).map((entry) => `"${entry.participant.name}"`);
        found.push(
          diagnostic(
            "OR-911",
            pointer("rankings", index),
            `Ranking "${ranking.label}" is ordered by the positions results publish, and ${without.length} of them carry none — ${names.join(", ")}${without.length > names.length ? " and others" : ""}. Every selected result then shares first place.`,
            `Publish a position for each result this ranking selects, or exclude the ones that have none.`
          )
        );
      }
    }
    ranking.sortBy.forEach((measureId, measureIndex) => {
      const definition = measure(document, measureId);
      if (definition === void 0) return;
      if (normalizeBetterWhen(definition.betterWhen) === "none") {
        found.push(
          diagnostic(
            "OR-301",
            pointer("rankings", index, "sortBy", measureIndex),
            `Ranking "${ranking.label}" sorts on "${definition.label}", which declares betterWhen: "none" — nothing says which direction wins.`,
            `Set betterWhen to "lower" or "higher" on the measure, or sort on a different one.`
          )
        );
      } else if (definition.kind === "text" || definition.kind === "boolean") {
        found.push(
          diagnostic(
            "OR-305",
            pointer("rankings", index, "sortBy", measureIndex),
            `Ranking "${ranking.label}" sorts on "${definition.label}", a ${definition.kind} measure. Only numeric kinds may decide an order: text has no ordering two consumers would agree on.`,
            `Declare a numeric measure for the ordering and keep "${definition.id}" as an attribute.`
          )
        );
      }
    });
    const entries = rank(document, ranking.id);
    if (ranking.scope?.event === void 0) {
      const eventsSeen = new Set(entries.map((entry) => entry.result.event ?? ""));
      const carryingNone = entries.filter(
        (entry) => entry.rank === null && ranking.sortBy.every((id) => entry.values[id] === void 0)
      );
      if (eventsSeen.size > 1 && carryingNone.length > 0) {
        found.push(
          diagnostic(
            "OR-913",
            pointer("rankings", index),
            `Ranking "${ranking.label}" gathers results from ${eventsSeen.size} events and does not say which it means, so ${carryingNone.length} of them — carrying none of the measures it sorts on — follow the standings with no position, repeating competitors already listed.`,
            `Add "scope": { "event": "…" } naming the event this standing belongs to, alongside any category it selects.`
          )
        );
      }
    }
    const droppedForMeasure = entries.filter((entry) => {
      if (entry.rank !== null) return false;
      const excluded = ranking.excludeStatuses ?? DEFAULT_EXCLUDED_STATUSES;
      if (excluded.includes(normalizeStatus(entry.result.status))) return false;
      const present = ranking.sortBy.filter((id) => entry.values[id] !== void 0).length;
      return present > 0 && present < ranking.sortBy.length;
    });
    if (droppedForMeasure.length > 0) {
      const names = droppedForMeasure.slice(0, 3).map((entry) => entry.participant.name).join(", ");
      const more = droppedForMeasure.length > 3 ? `, and ${droppedForMeasure.length - 3} more` : "";
      found.push(
        diagnostic(
          "OR-908",
          pointer("rankings", index),
          `Ranking "${ranking.label}" leaves ${droppedForMeasure.length} ${droppedForMeasure.length === 1 ? "result" : "results"} unranked because ${droppedForMeasure.length === 1 ? "it lacks" : "they lack"} a measure it sorts on — ${names}${more}. ${droppedForMeasure.length === 1 ? "It is" : "They are"} not excluded by status, so this is probably not intended.`,
          `Publish a value every selected result carries, copying earlier rounds forward where a later one did not happen — or narrow the ranking's scope.`
        )
      );
    }
    if (entries.length === 0) {
      found.push(
        diagnostic(
          "OR-906",
          pointer("rankings", index),
          `Ranking "${ranking.label}" selects no result at all.`,
          `Check its scope: the event or category may hold no results.`
        )
      );
    }
    if (normalizeTies(ranking.ties) === "strict") {
      const tied = entries.filter((entry) => entry.rank !== null && entry.tiedWith.length > 0);
      if (tied.length > 0) {
        const names = tied.map((entry) => entry.participant.name).join(", ");
        found.push(
          diagnostic(
            "OR-302",
            pointer("rankings", index, "ties"),
            `Ranking "${ranking.label}" declares ties: "strict", but ${names} finish level on every sorting measure.`,
            `Add a tie-break measure to sortBy, or switch ties to "standard" or "dense".`
          )
        );
      }
    }
  });
  const declared = new Map((document.rankings ?? []).map((entry) => [entry.id, entry]));
  const derivedByRanking = /* @__PURE__ */ new Map();
  const derivedFor = (rankingId) => {
    let cached = derivedByRanking.get(rankingId);
    if (cached === void 0) {
      cached = new Map(rank(document, rankingId).map((entry) => [entry.result, entry.rank]));
      derivedByRanking.set(rankingId, cached);
    }
    return cached;
  };
  const isIdentifier = (id) => /^[A-Za-z0-9_-]+$/.test(id);
  document.results.forEach((result, index) => {
    for (const [rankingId, supplied] of Object.entries(result.ranks ?? {})) {
      const ranking = declared.get(rankingId);
      if (ranking === void 0 && !isIdentifier(rankingId)) continue;
      if (ranking === void 0) {
        found.push(
          diagnostic(
            "OR-201",
            pointer("results", index, "ranks", rankingId),
            `This result publishes a position in "${rankingId}", which is not a declared ranking.`,
            `Declare a ranking with id "${rankingId}", or remove this entry.`
          )
        );
        continue;
      }
      if (!Number.isInteger(supplied) || supplied < 1) continue;
      const derived = derivedFor(rankingId).get(result);
      if (derived === void 0 || derived === null) {
        const status = normalizeStatus(result.status);
        found.push(
          diagnostic(
            "OR-303",
            pointer("results", index, "ranks", rankingId),
            `This result publishes position ${supplied} in "${ranking.label}", but that ranking does not rank it — its status is "${status}", or it lacks a measure the ranking sorts on.`,
            `Remove this entry, or adjust the ranking's excludeStatuses if the result belongs in it.`
          )
        );
        continue;
      }
      if (derived !== supplied) {
        found.push(
          diagnostic(
            "OR-902",
            pointer("results", index, "ranks", rankingId),
            `The document states position ${supplied} in "${ranking.label}", but the measures derive ${derived}.`,
            `Check the tie-break rule, or drop the entry and let the position be derived.`
          )
        );
      }
    }
  });
  return found;
}
const SPDX_SHAPE = /^[A-Za-z0-9.+()-]+(?: (?:AND|OR|WITH) [A-Za-z0-9.+()-]+)*$/;
const NAMES_NOTHING = /* @__PURE__ */ new Set(["n", "#", "no"]);
function checkQuality(document) {
  const found = [];
  const usedMeasureIds = /* @__PURE__ */ new Set();
  const usedAttributeIds = /* @__PURE__ */ new Set();
  for (const key of Object.keys(document.attributes ?? {})) usedAttributeIds.add(key);
  for (const result of document.results) {
    for (const key of Object.keys(result.values ?? {})) usedMeasureIds.add(key);
    for (const key of Object.keys(result.attributes ?? {})) usedAttributeIds.add(key);
  }
  for (const participant of document.participants) {
    for (const key of Object.keys(participant.attributes ?? {})) usedAttributeIds.add(key);
  }
  for (const event of document.events ?? []) {
    for (const key of Object.keys(event.attributes ?? {})) usedAttributeIds.add(key);
  }
  for (const category of document.categories ?? []) {
    for (const key of Object.keys(category.attributes ?? {})) usedAttributeIds.add(key);
  }
  (document.measures ?? []).forEach((measure2, index) => {
    if (measure2.min !== void 0 && measure2.max !== void 0 && measure2.min > measure2.max) {
      found.push(
        diagnostic(
          "OR-109",
          pointer("measures", index, "min"),
          `"${measure2.label}" declares a minimum of ${measure2.min} and a maximum of ${measure2.max}, so no value can satisfy both.`,
          `Swap them, or drop whichever bound was not meant.`
        )
      );
    }
  });
  (document.measures ?? []).forEach((measure2, index) => {
    if (measure2.kind !== "count") return;
    if (!NAMES_NOTHING.has((measure2.unit ?? "").toLowerCase())) return;
    found.push(
      diagnostic(
        "OR-111",
        pointer("measures", index, "unit"),
        `"${measure2.label}" counts "${measure2.unit}", which names nothing. A count says what it counts — laps, matches, faults — and a figure counting nothing is an identifier somebody allocated rather than something anybody measured.`,
        `Name what is counted, or declare it in "attributes" with type "number" if it is a bib, a lane or a start number.`
      )
    );
  });
  const license = document.source?.license;
  if (license !== void 0 && !SPDX_SHAPE.test(license)) {
    found.push(
      diagnostic(
        "OR-912",
        "/source/license",
        `"${license}" is not an SPDX identifier, so nothing can tell whether the data may be reused without a person reading it.`,
        `Use the identifier for these terms — "CC-BY-4.0", "CC0-1.0", "ODbL-1.0" — or an SPDX expression such as "CC-BY-4.0 OR ODbL-1.0".`
      )
    );
  }
  const scales = new Map(
    (document.measures ?? []).filter((measure2) => measure2.min !== void 0 || measure2.max !== void 0).filter(
      (measure2) => !(measure2.min !== void 0 && measure2.max !== void 0 && measure2.min > measure2.max)
    ).map((measure2) => [measure2.id, measure2])
  );
  if (scales.size > 0) {
    document.results.forEach((result, index) => {
      for (const [id, value] of Object.entries(result.values ?? {})) {
        const measure2 = scales.get(id);
        if (measure2 === void 0 || typeof value !== "number") continue;
        const below = measure2.min !== void 0 && value < measure2.min;
        const above = measure2.max !== void 0 && value > measure2.max;
        if (!below && !above) continue;
        const bounds = measure2.min !== void 0 && measure2.max !== void 0 ? `${measure2.min} to ${measure2.max}` : measure2.min !== void 0 ? `${measure2.min} or more` : `${measure2.max} or less`;
        found.push(
          diagnostic(
            "OR-909",
            pointer("results", index, "values", id),
            `${value} falls outside the scale "${measure2.label}" declares (${bounds}).`,
            `Correct the value, or widen the scale if the bounds were wrong.`
          )
        );
      }
    });
  }
  const competed = new Set(document.results.map((result) => result.participant));
  const inTeam = new Set(document.participants.flatMap((participant) => participant.members ?? []));
  const anyResults = document.results.length > 0;
  document.participants.forEach((participant, index) => {
    if (!anyResults) return;
    if (competed.has(participant.id) || inTeam.has(participant.id)) return;
    found.push(
      diagnostic(
        "OR-910",
        pointer("participants", index),
        `"${participant.name}" is declared but holds no result and belongs to no team, so nothing in this document says what they did.`,
        `Give them a result — a status is enough for someone who did not start — or remove them.`
      )
    );
  });
  (document.measures ?? []).forEach((measure2, index) => {
    if (!usedMeasureIds.has(measure2.id)) {
      found.push(
        diagnostic(
          "OR-901",
          pointer("measures", index),
          `Measure "${measure2.label}" is declared but no result carries a value for it.`,
          `Remove it, or populate it — consumers will show an empty column otherwise.`
        )
      );
    }
  });
  (document.attributeDefinitions ?? []).forEach((attribute, index) => {
    if (!usedAttributeIds.has(attribute.id)) {
      found.push(
        diagnostic(
          "OR-905",
          pointer("attributes", index),
          `Attribute "${attribute.label}" is declared but never used.`,
          `Remove it, or attach it to the entities it describes.`
        )
      );
    }
  });
  (document.categories ?? []).forEach((category, index) => {
    const members = category.participants ?? [];
    const selects = document.results.some((result) => members.includes(result.participant));
    if (!selects) {
      found.push(
        diagnostic(
          "OR-907",
          pointer("categories", index),
          `Category "${category.label}" selects no result: none of its participants has one.`,
          `Populate it, remove it, or check that its participant ids are right.`
        )
      );
    }
  });
  const carriesText = document.description !== void 0 || document.results.some((result) => result.notes !== void 0);
  if (document.lang === void 0 && carriesText) {
    found.push(
      diagnostic(
        "OR-903",
        pointer(),
        `The document carries prose but does not declare its language.`,
        `Add "lang" with a BCP 47 tag, for example "lang": "en".`
      )
    );
  }
  const fields = new Map(
    (document.events ?? []).filter((event) => event.participants !== void 0).map((event) => [event.id, new Set(event.participants)])
  );
  document.results.forEach((result, index) => {
    if (result.event === void 0) return;
    const field = fields.get(result.event);
    if (field !== void 0 && !field.has(result.participant)) {
      found.push(
        diagnostic(
          "OR-904",
          pointer("results", index, "participant"),
          `"${result.participant}" has a result for event "${result.event}" but is absent from that event's declared field.`,
          `Add the participant to the event's "participants" list, or remove that list if the field is open.`
        )
      );
    }
  });
  return found;
}
function validate(input, options = {}) {
  const { strict = false, schemaOnly = false } = options;
  const decoded = decode(input);
  if ("fatal" in decoded) {
    return report([decoded.fatal], strict);
  }
  const value = decoded.value;
  const found = [...checkSchema(value)];
  if (!schemaOnly) {
    let document;
    try {
      document = parse(value);
    } catch {
      document = void 0;
    }
    if (document !== void 0) {
      found.push(...checkReferences(document));
      found.push(...checkRanking(document));
      found.push(...checkQuality(document));
    }
  }
  return report(found, strict);
}
function decode(input) {
  let value = input;
  if (typeof input === "string") {
    try {
      value = JSON.parse(input);
    } catch (error) {
      return {
        fatal: diagnostic(
          "OR-401",
          "/",
          `The input is not valid JSON: ${error.message}`,
          `Check for a trailing comma, an unquoted key, or a truncated file.`
        )
      };
    }
  }
  if (!isOpenResult(value)) {
    return {
      fatal: diagnostic(
        "OR-401",
        "/openresult",
        `This is not an OpenResult document: the "openresult" member is missing or is not a MAJOR.MINOR version string.`,
        `Add "openresult": "${SUPPORTED_VERSION.major}.${SUPPORTED_VERSION.minor}" at the top level.`
      )
    };
  }
  try {
    parse(value);
  } catch (error) {
    if (error instanceof UnsupportedVersionError) {
      return {
        fatal: diagnostic(
          "OR-402",
          "/openresult",
          error.message,
          `Use a consumer that supports ${error.found}, or ask the producer for a ${error.supported} document.`
        )
      };
    }
  }
  return { value };
}
function report(found, strict) {
  const errors = found.filter((entry) => entry.severity === "error");
  const warnings = found.filter((entry) => entry.severity === "warning");
  return {
    valid: strict ? errors.length === 0 && warnings.length === 0 : errors.length === 0,
    errors,
    warnings
  };
}
export {
  CATALOGUE,
  validate
};
//# sourceMappingURL=index.js.map
